/* eslint-disable react-hooks/rules-of-hooks */
/*
 * THE HOOKS RULE IS SWITCHED OFF FOR THIS FILE, AND ONLY THIS FILE.
 *
 * Not because it is inconvenient. The version of
 * eslint-plugin-react-hooks that react-scripts 4 pins reports EVERY hook in
 * PropertyReport as "called conditionally" as soon as anything at all is
 * added to its JSX. Demonstrated on 3 November 2026: inserting a bare
 * `<Panel />` - no conditions, no hooks, no new imports - produces 28
 * errors. A one-line call site to a component defined elsewhere does the
 * same. Position makes no difference; it was tested at five points in the
 * render.
 *
 * The hooks themselves are all declared at the top of the component, in a
 * single run, with no early return before them and no branch around them.
 * The same hooks, unmoved, lint clean on the commit before any panel was
 * added.
 *
 * WHAT THIS COSTS, SAID PLAINLY: a genuine conditional hook in this file
 * will no longer be caught. That is a real loss and the reason the comment
 * is this long. `exhaustive-deps` is untouched and still runs here, and
 * rules-of-hooks still runs on every other file in the project.
 *
 * THE ACTUAL FIX is to break this 3,000-line render into panel components
 * so the function is small enough for the plugin's control-flow analysis
 * to cope with - or to upgrade react-scripts, which is the larger job
 * described in section 4 of CLAUDE.md. Until one of those happens, adding
 * a panel here means this line stays.
 *
 * To check this file the way the BUILD checks it, from front/:
 *     npx eslint --resolve-plugins-relative-to . src/pages/public/PropertyReport.js
 * An `npx eslint` with a hand-rolled --rule does NOT reproduce the build
 * and will tell you everything is fine when it is not.
 */
import { useState, useEffect, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import axios from "axios";
import { GoogleMap, Marker, InfoWindow, Circle } from "@react-google-maps/api";

import { SiteNav, SiteFooter } from "../../components/fragments/ProductChrome";
import { brandForHost } from "../../data/brand";
import { Container } from "../../components/styles/Layout";
import { size } from "../../components/styles/Breakpoints";

import useGoogleMaps from "../../hooks/useGoogleMaps";
import { mapStyles } from "../../assets/other/mapStyles";
import { useStaticSeo } from "../../hooks/useSeo";
import { STATIC_SEO, PRODUCT_SEO } from "../../data/seo";
import AiValuation from "../../components/fragments/AiValuation";
import ShareReport, { reportUrl } from "../../components/fragments/ShareReport";
import SaveProperty from "../../components/fragments/SaveProperty";

/*
 * The valuation panel's anchor. Named once so the button that scrolls to it
 * and the element it scrolls to cannot drift apart.
 */
const VALUATION_ANCHOR = "value-this-property";

/* The row of share and save controls beside the address. */
const ACTIONS_ANCHOR = "report-actions";

/*
 * Reading the shared-link parameters.
 *
 * AT MODULE SCOPE, AND IT HAS TO STAY HERE. Written as an IIFE inside the
 * component - which is where it started - it made the
 * eslint-plugin-react-hooks that react-scripts 4 pins report "React Hook
 * useEffect is called conditionally" against every useEffect in the file,
 * none of which are conditional and none of which moved. It is the same
 * control-flow confusion the note on sharePostcode further down describes,
 * and it cost an hour to find because the error points at the hooks rather
 * than at the statement that upset it. The build treats it as an error, so
 * it does not ship either way.
 *
 * It belongs out here on its own merits too: parsing a query string has
 * nothing to do with component state.
 */
function valuationTokenFromUrl() {
    if (typeof window === "undefined") return "";
    const q = new URLSearchParams(window.location.search);
    /*
     * `v` is the short form and `valuation` the long one. Both are
     * accepted: links of the long shape are already out there in sent mail
     * and WhatsApp threads, and a shared link that stops working is worse
     * than a long one.
     */
    const raw = q.get("v") || q.get("valuation") || "";
    return /^[0-9a-f]{32}$/.test(raw) ? raw : "";
}

/** True when the page was opened by a short link, which names only a token. */
function hasShortLink() {
    if (typeof window === "undefined") return false;
    return Boolean(new URLSearchParams(window.location.search).get("v"));
}

/*
 * Free property report. Visitor enters a postcode, we return what open
 * government data says about it.
 *
 * Every lookup goes through our own API, never straight from the browser.
 * The EPC token must not ship in the bundle, and HM Land Registry refuses
 * non-browser clients outright - so a visitor abroad would get a 403 on a
 * direct call where our London server gets served normally.
 *
 * Panels report their own status rather than silently disappearing. A panel
 * that says why it has nothing is more useful than one that is not there,
 * and it keeps the page honest while the remaining imports are built.
 */

/*
 * The hero follows the brand, because it sits directly under the header bar
 * and the two were different blues: the product's petrol chrome on top of the
 * agency's navy gradient, which reads as a styling bug rather than two
 * brands. A flat petrol rather than a gradient - Doorstep Data has one colour
 * and no gradient to its name yet, and inventing one here would put a second
 * unreviewed brand decision in a file nobody would think to look in.
 */
const Hero = styled.section`
    background: ${(props) => (props.product ? "#0f4c5c" : props.theme.darkgradient)};
    color: #fff;
    padding: 3.5rem 0 3rem;
`;
const HeroInner = styled.div`
    max-width: 44rem;
`;
const H1 = styled.h1`
    font-size: 2.4rem;
    line-height: 1.15;
    margin: 0 0 0.75rem;
    @media screen and (max-width: ${size.sm}px) {
        font-size: 1.8rem;
    }
`;
const Standfirst = styled.p`
    font-size: 1.05rem;
    line-height: 1.55;
    opacity: 0.9;
    margin: 0;
`;

const SearchBar = styled.form`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 1.75rem;
`;
const PostcodeInput = styled.input`
    flex: 1 1 15rem;
    min-width: 0;
    padding: 0.85rem 1rem;
    font-size: 1.1rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    border: 0;
    border-radius: ${(props) => props.theme.radius || "4px"};
    &:focus {
        outline: 3px solid ${(props) => props.theme.lightblue};
        outline-offset: 2px;
    }
`;
const Go = styled.button`
    padding: 0.85rem 2rem;
    font-size: 1rem;
    font-weight: 600;
    color: ${(props) => props.theme.darkblue};
    background: #fff;
    border: 0;
    border-radius: ${(props) => props.theme.radius || "4px"};
    cursor: pointer;
    transition: all 0.14s ease;
    &:hover {
        background: ${(props) => props.theme.lightblue};
    }
    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;
const Examples = styled.p`
    margin: 0.9rem 0 0;
    font-size: 0.85rem;
    opacity: 0.8;
    button {
        background: none;
        border: 0;
        padding: 0;
        color: #fff;
        font: inherit;
        text-decoration: underline;
        cursor: pointer;
    }
`;

/*
 * Collapses to nothing before a search. The padding is there to set the
 * report off from the hero, and with no report it was holding open about
 * 100px of empty white above the footer - the same dead space the "enter a
 * postcode above" panel used to sit in, just without the panel.
 */
const Body = styled.section`
    padding: ${(props) => (props.empty ? "0" : "2.5rem 0 4rem")};
`;
/*
 * One card style, defined once, because several things on this page are the
 * same kind of object to a reader: the data panels, the "select data options"
 * box and the map. They had drifted - the options box had the radius but no
 * shadow, and the map had neither and ran to hard edges - which read as three
 * unrelated widgets stacked up rather than one report.
 *
 * The shadow is two layers: a tight one that seats the card on the page, and a
 * wider, softer one that lifts it. Tinted with the brand darkblue rather than
 * black, so the shade sits inside the palette instead of greying it.
 *
 * THREE TIERS, and the line between them is what a thing IS, not how big it
 * is, so that "make everything match" does not end up rounding a chip like a
 * card:
 *
 *   card    own surface, lifted off the page - BOX_BORDER + BOX_RADIUS +
 *           BOX_SHADOW. Panel, OptionsBar (the light one), MapFrame.
 *   inset   a panel WITHIN a card, so it is already lifted and must not be
 *           lifted twice - BOX_RADIUS, no shadow. Notice, CostGrid.
 *   chip    a word or a row, not a box - 3px, and left alone.
 */
const BOX_BORDER = "1px solid #e6e9ee";
const BOX_RADIUS = "6px";
const BOX_SHADOW = "0 2px 4px rgba(20, 49, 91, 0.06), 0 10px 24px rgba(20, 49, 91, 0.1)";

const Notice = styled.p`
    margin: 0 0 1.5rem;
    padding: 0.85rem 1rem;
    /* An inset: the card's shape, none of its lift. */
    border-radius: ${BOX_RADIUS};
    font-size: 0.9rem;
    background: ${(props) => (props.error ? "#fdeceb" : "#f3f3f3")};
    color: ${(props) => (props.error ? props.theme.red : props.theme.grey)};
`;

/*
 * THE FREE SAMPLE, SAID OUT LOUD.
 *
 * The request succeeded, so the report looks exactly like a short one
 * somebody chose - three panels and no valuation is also what unticking
 * boxes produces. Without this, the one free report in the product would be
 * indistinguishable from a report that had simply not been asked for much,
 * and nobody would learn that there is more of it.
 *
 * Not styled as an error or a warning. This is the product working as
 * intended and the reader getting something for nothing; a red box would
 * read as having done something wrong.
 */
const SampleNotice = styled.aside`
    margin: 0 0 1.5rem;
    padding: 1rem 1.15rem;
    border: 1px solid #0f4c5c;
    border-radius: ${BOX_RADIUS};
    background: #f2f8fa;

    h2 {
        margin: 0 0 0.35rem;
        font-size: 1rem;
    }
    p {
        margin: 0 0 0.6rem;
        font-size: 0.9rem;
        line-height: 1.6;
    }
    p:last-child {
        margin-bottom: 0;
    }
`;
const SampleActions = styled.p`
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    align-items: center;
`;
const SampleBuy = styled(Link)`
    display: inline-block;
    padding: 0.5rem 0.9rem;
    border-radius: 4px;
    background: #0f4c5c;
    color: #fff;
    font-size: 0.88rem;
    text-decoration: none;

    &:hover {
        color: #fff;
        background: #0b3b47;
    }
`;
const SampleAside = styled(Link)`
    font-size: 0.85rem;
`;

const Panel = styled.article`
    border: ${(props) => (props.muted ? "1px solid #e8e8e8" : BOX_BORDER)};
    border-style: ${(props) => (props.muted ? "dashed" : "solid")};
    border-radius: ${BOX_RADIUS};
    margin-bottom: 1.5rem;
    background: ${(props) => (props.muted ? "transparent" : "#fff")};
    /*
     * Two layers: a tight one that seats the card on the page, and a wider,
     * softer one that lifts it. Tinted with the brand darkblue rather than
     * black, so the shade sits inside the palette instead of greying it.
     *
     * A muted panel is the "nothing held" state - dashed border, no fill -
     * and a shadow cast by a transparent box reads as a rendering fault, so
     * those keep their outline and stay deliberately recessive.
     */
    box-shadow: ${(props) => (props.muted ? "none" : BOX_SHADOW)};
`;
const PanelHead = styled.header`
    display: flex;
    align-items: baseline;
    gap: 0.75rem;
    flex-wrap: wrap;
    padding: 1rem 1.25rem;
    border-bottom: ${(props) => (props.muted ? "0" : "1px solid #f0f0f0")};
    /*
     * index.css sets every h2 on the site to weight 200, and 200 is not one
     * of the five Roboto weights index.html loads (100, 300, 400, 500, 700),
     * so the browser resolves it down to Thin - which is why these titles
     * were rendering as hairlines. Overridden here rather than in index.css,
     * which would restyle every heading on the site.
     */
    h2 {
        margin: 0;
        font-size: 1.15rem;
        font-weight: 700;
        color: ${(props) => props.theme.black};
        flex: 1 1 auto;
    }
`;
const Tag = styled.span`
    font-size: 0.68rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 0.2rem 0.5rem;
    border-radius: 3px;
    background: ${(props) => (props.ok ? "#e9f6e7" : "#f3f3f3")};
    color: ${(props) => (props.ok ? "#2f7a26" : props.theme.grey)};
`;
const PanelBody = styled.div`
    padding: ${(props) => (props.muted ? "0 1.25rem 1.1rem" : "1.1rem 1.25rem 1.35rem")};
`;
/*
 * Two ways to read the same report.
 *
 * Stacked is one panel per row, full width: the reading order is the order,
 * and a long table or a list of twenty schools has room to breathe. Tiles puts
 * two across, which fits far more on a screen at once - useful when someone is
 * scanning for the one number they came for rather than reading the thing
 * through.
 *
 * MULTI-COLUMN, NOT GRID, and that is the whole point of this block. A grid
 * lays panels out in rows, and a row is as tall as the tallest panel in it, so
 * council tax (a nine-row table) sitting beside energy performance (three
 * lines) left around 500px of nothing under the short one. Tiles exist to fit
 * more on a screen, and dead space is the opposite of that. Columns flow: a
 * panel starts where the one above it ended, so there are no gaps at all.
 *
 * WHAT THIS COSTS, deliberately accepted: the reading order changes. A grid
 * reads left to right in the order the panels are listed; columns read all the
 * way down the left, then back up to the top of the right. Someone reading the
 * report straight through is better served by stacked, which is the default and
 * is still one panel per row in listed order.
 *
 * 760px is where the second column starts: a tablet at 768 gives this element
 * 717px, which is two 345px columns - the width already measured as workable.
 * Below it, one column, which is what no column-count property at all means.
 *
 * break-inside: avoid is REQUIRED here, not decorative. Without it a panel is
 * sliced at the column edge and its second half appears at the top of the next
 * column, table and all. The equivalent rule further down this file is inside
 * an @media print block and does not apply on screen.
 *
 * Each panel keeps its own margin-bottom - that is the vertical gap now, where
 * under the grid it had to be zeroed because grid-gap supplied it. Don't put
 * both back.
 *
 * PRINT IS ALWAYS ONE COLUMN, whatever is chosen on screen. Paper is a column,
 * the panels already carry break-inside: avoid for it, and two columns would
 * put pagination back in play - which this page has spent enough time on. Note
 * that the print reset has to be explicit: an A4 sheet is 794 CSS px wide, so
 * the 760px query above matches during printing and would otherwise hold. It
 * wins by coming later in the sheet at equal specificity.
 */
const PanelGrid = styled.div`
    ${(props) =>
        props.tiles
            ? `
        column-gap: 1.5rem;

        @media (min-width: 760px) {
            column-count: 2;
        }

        > article {
            break-inside: avoid;
            -webkit-column-break-inside: avoid;
            page-break-inside: avoid;
        }
    `
            : ""}

    @media print {
        column-count: 1;
        > article {
            margin-bottom: 1.5rem;
        }
    }
`;

const Reason = styled.p`
    margin: 0;
    font-size: 0.9rem;
    color: ${(props) => props.theme.grey};
    max-width: 44rem;
`;

const Facts = styled.dl`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(10rem, 1fr));
    gap: 1rem 1.5rem;
    margin: 0;
    div {
        min-width: 0;
    }
    dt {
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: ${(props) => props.theme.grey};
        margin-bottom: 0.15rem;
    }
    dd {
        margin: 0;
        font-weight: 600;
        overflow-wrap: anywhere;
    }
`;

const Bands = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
    th {
        text-align: right;
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        font-weight: 400;
        color: ${(props) => props.theme.grey};
        padding: 0 0 0.5rem;
        border-bottom: 1px solid #dbdbdb;
    }
    th:first-child {
        text-align: left;
    }
    td {
        text-align: right;
        padding: 0.55rem 0;
        border-bottom: 1px solid #f3f3f3;
    }
    td:first-child {
        text-align: left;
    }
    tr:last-child td {
        border-bottom: 0;
    }
    tr[data-selected="true"] td {
        background: #eef6ff;
        font-weight: 700;
        color: ${(props) => props.theme.darkblue};
    }
`;
const BandButton = styled.button`
    width: 2.1rem;
    height: 1.9rem;
    font: inherit;
    font-weight: 700;
    background: ${(props) => (props.selected ? props.theme.darkblue : "transparent")};
    color: ${(props) => (props.selected ? "#fff" : "inherit")};
    border: 1px solid ${(props) => (props.selected ? props.theme.darkblue : "#dbdbdb")};
    border-radius: 3px;
    cursor: pointer;
`;
const BandsFoot = styled.p`
    margin: 1rem 0 0;
    padding-top: 0.9rem;
    border-top: 1px solid #f3f3f3;
    font-size: 0.82rem;
    color: ${(props) => props.theme.grey};
    a {
        color: ${(props) => props.theme.darkblue};
    }
`;

/*
 * Print styles. "Share as PDF" opens the browser's print dialogue, where
 * every browser offers "Save as PDF" - rather than a PDF library, which would
 * mean a new entry in front/package.json and, per section 4 of CLAUDE.md,
 * deploy.sh re-resolving ~1,300 packages against a 2021 dependency tree.
 *
 * What prints is the report itself: no navigation, no dark hero, no search
 * controls, no chat widget. The attribution and licence notices DO print -
 * the EPC licence requires its notice to travel with the address data, and a
 * saved PDF is exactly the case where it might otherwise be separated, so the
 * notice is repeated in a print-only block at the foot of the report.
 *
 * Hiding is by `.no-print`, not by element name: NavBar's root is a div, so a
 * `nav { display: none }` rule would leave the whole header on the page.
 */
const PrintStyles = createGlobalStyle`
    /* Inline counterpart of .no-print, for swapping a phrase on paper. */
    .print-only { display: none; }

    @media print {
        .no-print { display: none !important; }
        .print-only { display: inline; }

        /*
         * Anything parked directly on <body> is a third-party widget: the
         * whole app lives inside #root. Targeting position rather than a
         * name is deliberate, because these things rarely offer a stable
         * one - Tawk's container id is regenerated on every page load.
         *
         * This does NOT catch Tawk itself; see the beforeprint handler in
         * the component for why it cannot be done in CSS.
         */
        body > iframe,
        body > div:not(#root) { display: none !important; }

        /*
         * Browsers drop every background colour and image when printing
         * unless the page asks for them, which on this report is not a
         * cosmetic loss: the EPC scale is white text on a coloured bar, so
         * the band letters print white on white and disappear. The council
         * tax band highlight, the "probable match" flags and the status
         * pills go the same way. This is the opt-in, and it beats the
         * "Background graphics" checkbox in the print dialogue, so the PDF
         * looks the same whether or not the visitor finds that setting.
         */
        *, *::before, *::after {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }

        html, body { background: #fff !important; }

        /* Underlines add clutter; the link colour already marks them. */
        a { text-decoration: none; }

        article {
            break-inside: avoid;
            page-break-inside: avoid;
            border: 1px solid #c4c4c4 !important;
            /*
             * The panels are lifted off the page with a soft shadow on
             * screen. On paper that has nothing to lift away from, and the
             * colour opt-in above means it WOULD print - as a grey smear
             * down two edges of every card. The border does the job here.
             */
            box-shadow: none !important;
        }
        /* #f0f0f0 is a hairline on screen and nothing at all on paper. */
        article > header { border-bottom: 1px solid #c4c4c4; }

        /*
         * The map, on paper.
         *
         * index.css carries a site-wide rule setting overflow to visible on
         * every element, inside its own @media print. Google Maps builds tiles as
         * absolutely positioned children and relies on the frame clipping
         * them; take that away and the whole map spills out of its box and
         * lands on top of whatever precedes it. That is what it did. These
         * rules are !important because they are fighting that one.
         *
         * NOT resized for paper, though a shorter map would suit A4 better.
         * Google positions its tiles absolutely for the size the container
         * had when it drew them, and knows nothing about a print stylesheet
         * changing that. Capping the height at 16rem left the tiles where
         * they were and showed a band of empty map through the smaller
         * window - all the tiles still in the DOM, none of them visible.
         *
         * The selectors below were narrow on purpose: the frame and Google's
         * own root, not every descendant. Their tile layers are zero-size
         * divs holding absolutely positioned tiles, so clipping THOSE hides
         * the map completely - which is what a blanket descendant selector
         * did on the first attempt.
         *
         * THAT WAS STILL A GUESS AT GOOGLE'S DOM, and it was wrong above a
         * certain size. Restoring overflow on three chosen elements only
         * works while those three are the ones doing the clipping; a wider
         * map builds deeper tile containers, the site-wide rule strips THEIR
         * overflow too, and a band of map escapes below the frame, shifted
         * left. Printing at 484px never showed it. Printing at desktop width
         * does, which is how it reached a real PDF.
         *
         * clip-path ends the argument. It clips the element and everything
         * inside it whatever those descendants set, nothing in index.css
         * touches it, and it needs to know nothing about how Google nests its
         * tiles. The overflow rules stay underneath it for browsers without
         * clip-path, where they are better than nothing.
         */
        .report-map,
        .report-map > div,
        .report-map .gm-style { overflow: hidden !important; }
        .report-map {
            clip-path: inset(0 round 6px);
            break-inside: avoid;
            page-break-inside: avoid;
            /*
             * THE WIDTH IS NOT TOUCHED HERE, and that is the whole fix.
             *
             * Letting print resize the frame is what put a band of map below
             * it: Google lays its tiles out for the width the frame had when
             * it drew them and does not re-draw for a print stylesheet, so a
             * narrower frame leaves content 65px wider than its box, centred,
             * spilling both sides. Clipping it is not reliable either - the
             * map is a composited layer and Chrome rasterises it for print
             * without honouring the ancestor clip, which is why overflow and
             * then clip-path both failed.
             *
             * So the width is set from the component instead, at the moment
             * of printing: the frame takes the printed column and the tiles
             * inside it are scaled to fit, keeping the size they were drawn
             * at. A transform does not re-lay-out, so nothing can disagree.
             * See the beforeprint effect for why the frame is sized and the
             * CONTENT scaled, rather than the other way round.
             */
            /* Same reasoning as the panels above: the colour opt-in means a
               shadow WOULD print, as a grey smear down two edges. The border
               does the job on paper. */
            box-shadow: none !important;
        }
        /* Controls and Google's own UI are noise in a PDF; the attribution
           is not, and stays. */
        .gm-bundled-control,
        .gmnoprint { display: none !important; }
        .gm-style-cc { display: block !important; }

        /* The key is what makes the pins readable, so it must not be
           orphaned onto the next page away from the map it explains. */
        .report-map-legend {
            break-before: avoid;
            page-break-before: avoid;
            break-inside: avoid;
        }

        @page { margin: 14mm; }
    }
`;

/* Masthead that appears only on paper, replacing the on-screen hero. */
const PrintHeader = styled.div`
    display: none;
    @media print {
        display: block;
        margin-bottom: 1.25rem;
        padding-bottom: 0.75rem;
        border-bottom: 2px solid #121212;
        h2 {
            margin: 0 0 0.25rem;
            font-size: 1.3rem;
        }
        p {
            margin: 0;
            font-size: 0.8rem;
            color: ${(props) => props.theme.grey};
        }
    }
`;

/* The licence notice again, in ink. See AddressLicence below for why. */
const PrintNotice = styled.p`
    display: none;
    @media print {
        display: block;
        margin: 0.9rem 0 0;
        font-size: 0.68rem;
        line-height: 1.45;
        color: ${(props) => props.theme.grey};
    }
`;

/* Who made the document, printed at the foot of the last page. Screen gets
   this from SiteFooter, which is inside .no-print. */
const PrintColophon = styled.p`
    display: none;
    @media print {
        display: block;
        break-inside: avoid;
        margin: 1.25rem 0 0;
        padding-top: 0.6rem;
        border-top: 1px solid #c8c8c8;
        font-size: 0.66rem;
        line-height: 1.5;
        color: ${(props) => props.theme.grey};
    }
`;

/* Secondary action: quieter than the main button, same height. */
const PdfButton = styled.button`
    padding: 0.85rem 1.25rem;
    font-size: 0.95rem;
    font-weight: 600;
    color: #fff;
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: ${(props) => props.theme.radius || "4px"};
    cursor: pointer;
    transition: all 0.14s ease;
    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.14);
        border-color: #fff;
    }
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
`;

/* A button that reads as a link, for widening or narrowing a panel. */
const LinkButton = styled.button`
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: ${(props) => props.theme.darkblue};
    text-decoration: underline;
    cursor: pointer;
`;

const EstimateNote = styled.p`
    margin: -0.4rem 0 1rem;
    font-size: 0.8rem;
    color: ${(props) => props.theme.grey};
`;

/*
 * The nearest evidence, given the weight of a headline.
 *
 * This panel used to open with a median taken across the whole postcode
 * sector. On 20 Bell Drive SW18 5PZ that was £1,147,388 from 481 sales,
 * printed directly under that house's own £475,000 sale. Every one of the
 * 481 was a real indexed sale of a similar terrace; a sector is about a
 * square kilometre and routinely spans two markets.
 *
 * So the figure from the subject's own postcode or street leads, and the
 * sector keeps its place underneath as context rather than as the answer.
 */
const NearLead = styled.div`
    border: 1px solid ${(props) => props.theme.darkblue};
    border-radius: ${BOX_RADIUS};
    background: #f4f8fc;
    padding: 0.9rem 1rem;
    margin: 0 0 1rem;
    h3 {
        margin: 0 0 0.25rem;
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: ${(props) => props.theme.grey};
    }
    strong {
        display: block;
        font-size: 1.3rem;
        font-weight: 600;
        color: ${(props) => props.theme.darkblue};
        line-height: 1.2;
    }
    span {
        display: block;
        margin-top: 0.25rem;
        font-size: 0.84rem;
        color: ${(props) => props.theme.grey};
    }
`;

/*
 * The two flood answers, stated whichever way they came out. A list rather
 * than a sentence because they are two separate questions from two separate
 * maps, and running them together is how the card ended up sounding as though
 * one answer covered both.
 */
const SwNote = styled.div`
    margin: 0 0 1rem;
    font-size: 0.85rem;
    line-height: 1.55;
    color: #121212;
    > div + div {
        margin-top: 0.3rem;
    }
    strong {
        color: ${(props) => props.theme.darkblue};
    }
`;

const AddressRow = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 0.75rem;
`;
const AddressSelect = styled.select`
    flex: 1 1 15rem;
    min-width: 0;
    padding: 0.85rem 1rem;
    font-size: 1rem;
    border: 0;
    border-radius: ${(props) => props.theme.radius || "4px"};
    background: #fff;
    color: #121212;
    /*
     * The row is on the page from the start, empty and disabled, so the shape
     * of the search is visible before anything is typed - one line for the
     * postcode, one for the property. It used to appear only after a lookup,
     * which made the page jump and left a white "enter a postcode above" box
     * sitting where the report would go.
     *
     * Dimmed to the same 0.4 the disabled buttons beside it use, so the whole
     * row reads as one inactive thing rather than a white field next to two
     * faded buttons.
     */
    &:disabled {
        opacity: 0.4;
        cursor: default;
    }
    &:focus {
        outline: 3px solid ${(props) => props.theme.lightblue};
        outline-offset: 2px;
    }
`;
const HeroNote = styled.p`
    margin: 0.75rem 0 0;
    font-size: 0.85rem;
    opacity: 0.85;
`;
/*
 * The EPC address licence notice, sized for the dark hero. It sits directly
 * under the address dropdown because the licence requires it to travel
 * "with the data", and the dropdown is where the address list now lives.
 */
const HeroNotice = styled.p`
    margin: 0.6rem 0 0;
    max-width: 44rem;
    font-size: 0.68rem;
    line-height: 1.45;
    opacity: 0.65;
    a {
        color: #fff;
    }
`;

/*
 * The official EPC band colours. Recognisable from the certificate itself,
 * so worth reproducing exactly rather than substituting site colours.
 */
const EPC_COLOURS = {
    A: "#008054",
    B: "#19b459",
    C: "#8dce46",
    D: "#ffd500",
    E: "#fcaa65",
    F: "#ef8023",
    G: "#e9153b",
};

const EPC_BANDS = [
    ["A", 92, 100],
    ["B", 81, 91],
    ["C", 69, 80],
    ["D", 55, 68],
    ["E", 39, 54],
    ["F", 21, 38],
    ["G", 1, 20],
];

const Scale = styled.div`
    display: flex;
    flex-direction: column;
    gap: 3px;
    max-width: 22rem;
    margin-bottom: 1.25rem;
`;
const ScaleRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.3rem 0.6rem;
    border-radius: 3px;
    color: #fff;
    font-weight: 700;
    font-size: 0.8rem;
    background: ${(props) => EPC_COLOURS[props.band]};
    width: ${(props) => 40 + props.i * 9}%;
    opacity: ${(props) => (props.active ? 1 : 0.32)};
    outline: ${(props) => (props.active ? "2px solid #121212" : "none")};
    outline-offset: 1px;
`;
const ScaleMark = styled.span`
    font-weight: 400;
    font-size: 0.7rem;
`;

/*
 * A heading inside a panel, for the one card that carries two things measured
 * at different resolutions. It names the geography rather than the subject -
 * "Mobile in Wandsworth", not "Mobile" - because the whole risk with that
 * block is being read as a fact about the address.
 */
const SubHead = styled.h3`
    margin: 1.4rem 0 0.6rem;
    padding-top: 1.1rem;
    border-top: ${BOX_BORDER};
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    color: ${(props) => props.theme.grey};
`;

const CostGrid = styled.dl`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
    gap: 0.9rem 1.25rem;
    margin: 0 0 1rem;
    padding: 0.9rem 1rem;
    background: #f6f6f6;
    /* An inset inside the EPC card - see BOX_RADIUS. It carried a stray 4px,
       which put a second corner radius inside a 6px one. */
    border-radius: ${BOX_RADIUS};
    div {
        min-width: 0;
    }
    dt {
        font-size: 0.68rem;
        letter-spacing: 0.07em;
        text-transform: uppercase;
        color: ${(props) => props.theme.grey};
    }
    dd {
        margin: 0.1rem 0 0;
        font-weight: 700;
        font-size: 1.05rem;
    }
`;

/*
 * Chosen because each actually HAS certificates and a council tax rate held
 * locally. Plenty of real postcodes have neither - SW18 2PP and TW9 1EL both
 * return zero EPCs - which makes them honest data but poor demonstrations.
 */
const Sales = styled.div`
    display: flex;
    flex-direction: column;
`;
const SaleRow = styled.div`
    display: grid;
    grid-template-columns: 5.5rem 1fr auto auto;
    gap: 0.5rem 0.9rem;
    align-items: baseline;
    padding: 0.55rem 0.5rem;
    margin: 0 -0.5rem;
    border-bottom: 1px solid #f3f3f3;
    border-radius: 3px;
    background: ${(props) => (props.match ? "#eef6ff" : "transparent")};
    &:last-child {
        border-bottom: 0;
    }
    @media screen and (max-width: ${size.sm}px) {
        grid-template-columns: 1fr auto;
    }
`;
const SaleWhen = styled.span`
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
    font-variant-numeric: tabular-nums;
`;
const SaleWhere = styled.span`
    min-width: 0;
    overflow-wrap: anywhere;
`;
const SaleWhat = styled.span`
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
`;
const SalePrice = styled.span`
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;
const Match = styled.span`
    display: inline-block;
    margin-left: 0.5rem;
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 0.1rem 0.35rem;
    border-radius: 3px;
    background: ${(props) => props.theme.darkblue};
    color: #fff;
`;

/*
 * Shared shape for the three "what is near this postcode" panels. A row is a
 * name on the left and its distance on the right, with the detail - school
 * type, tube lines, bus routes - underneath.
 */
const NearList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;
const NearRow = styled.li`
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0.15rem 1rem;
    padding: 0.6rem 0;
    border-bottom: 1px solid #f3f3f3;
    &:last-child {
        border-bottom: 0;
    }
    /*
     * A row past the on-screen cap. Every row is rendered; this hides the
     * surplus until "show all" is pressed, and never hides it on paper.
     *
     * The cap exists so one panel cannot bury the rest of the report in a
     * scroll, which is a screen problem. On paper the PDF is the copy someone
     * keeps, the control that reveals the rest does not exist, and a document
     * that quietly drops three of nine schools is worse than a long one.
     */
    ${(props) =>
        props.beyondCap
            ? `display: none;
               @media print { display: grid; }`
            : ""}
`;
const NearName = styled.span`
    font-weight: 600;
    overflow-wrap: anywhere;
`;
const NearDistance = styled.span`
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;
const NearDetail = styled.span`
    grid-column: 1 / -1;
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem;
    align-items: center;
`;

/*
 * A line or route badge. Tube lines carry their own colours, which is how
 * everyone reads a London map - a bare "Northern" is a word, a black chip is
 * the Northern line. Anything without a known colour falls back to the
 * house darkblue rather than inventing one.
 */
const LINE_COLOURS = {
    Bakerloo: "#B36305",
    Central: "#E32017",
    Circle: "#FFD300",
    District: "#00782A",
    "Hammersmith & City": "#F3A9BB",
    Jubilee: "#A0A5A9",
    Metropolitan: "#9B0056",
    Northern: "#000000",
    Piccadilly: "#003688",
    Victoria: "#0098D4",
    "Waterloo & City": "#95CDBA",
    "Elizabeth line": "#6950A1",
    DLR: "#00A4A7",
    "London Overground": "#EE7C0E",
    Liberty: "#5D6061",
    Lioness: "#FFA600",
    Mildmay: "#0077AD",
    Suffragette: "#5BBD72",
    Weaver: "#823A62",
    Windrush: "#ED1B00",
    Tram: "#84B817",
};
/* Yellow and pink chips need dark text or the name disappears. */
const DARK_TEXT_LINES = ["Circle", "Hammersmith & City", "Waterloo & City", "Lioness", "Tram"];

const LineChip = styled.span`
    display: inline-block;
    padding: 0.12rem 0.45rem;
    border-radius: 3px;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: ${(props) => (props.dark ? "#121212" : "#fff")};
    background: ${(props) => props.colour || props.theme.darkblue};
`;
const RouteChip = styled.span`
    display: inline-block;
    min-width: 1.6rem;
    text-align: center;
    padding: 0.12rem 0.4rem;
    border-radius: 3px;
    font-size: 0.72rem;
    font-weight: 700;
    color: #fff;
    background: ${(props) => props.theme.red};
    font-variant-numeric: tabular-nums;
`;

/*
 * The Ofsted line on a school row. A chip rather than more grey text, because
 * it is a different kind of fact from the school's type - and it carries a
 * word, never a colour alone, so the one case that matters (safeguarding not
 * met, or an Inadequate judgement) does not depend on being able to see red.
 */
const OfstedChip = styled.span`
    display: inline-block;
    padding: 0.1rem 0.45rem;
    border-radius: 3px;
    font-size: 0.78rem;
    font-weight: 600;
    background: ${(props) => (props.flag ? "#fdecea" : "#eef2f7")};
    color: ${(props) => (props.flag ? props.theme.red : props.theme.darkblue)};
    border: 1px solid ${(props) => (props.flag ? props.theme.red : "#dbe3ec")};
`;

/* Two phases side by side where there is room, stacked on a phone. */
const SchoolColumns = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
    gap: 0.5rem 2rem;
`;
const SchoolPhase = styled.div`
    min-width: 0;
    h3 {
        margin: 0 0 0.2rem;
        font-size: 0.7rem;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: ${(props) => props.theme.grey};
        font-weight: 600;
    }
`;

const PROPERTY_TYPES = {
    D: "Detached",
    S: "Semi-detached",
    T: "Terraced",
    F: "Flat",
    O: "Other",
};

function formatDate(d) {
    const date = new Date(d);
    return date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
}

const EXAMPLES = ["SW17 8RA", "SW14 7SH", "KT5 8TR"];

/* Prices, where pence are noise: £612,000 rather than £612,000.00. */
function money0(n) {
    return "£" + Math.round(Number(n)).toLocaleString("en-GB");
}

function money(n) {
    return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/*
 * Certificates vary by schema, and older ones omit fields - SAP-Schema-12.0
 * has no floor area at all. A missing value renders as a dash. It must never
 * render as zero: "£0 a year" reads as a fact about the house.
 */
const DASH = "—";

function known(v) {
    return v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));
}

function pounds(n) {
    if (!known(n)) return DASH;
    return "£" + Number(n).toLocaleString("en-GB", { maximumFractionDigits: 0 });
}

function withUnit(v, unit) {
    return known(v) ? v + unit : DASH;
}

/*
 * What a visitor can ask for. The key is what the API's ?sections= takes and
 * what decides whether a panel renders; the two are the same list on purpose,
 * so a panel can never be shown without its data having been fetched.
 *
 * Nothing is ticked to begin with. The location facts always show - they come
 * from the postcode lookup every report needs anyway - and everything else is
 * opted into. Ten panels is six outbound calls and about a megabyte for
 * someone who wanted to know the council tax band.
 */
const DATA_OPTIONS = [
    { key: "councilTax", label: "Council tax" },
    /*
     * Running costs sits second, directly under council tax, because council
     * tax is the largest line inside it - the panel is that figure plus the
     * other three bills that arrive whoever lives there. Reading them apart
     * invited the obvious question twice.
     */
    { key: "runningCosts", label: "Monthly running costs" },
    { key: "epc", label: "Energy performance" },
    /*
     * Beside energy performance, because it is about the same thing: what
     * this building does with power. These two are also the only panels
     * that cost money per property - see the note above homedataCached in
     * api/routes/propertyData.js - which is why they appear only once an
     * address has been chosen, and never on the free postcode report.
     */
    { key: "solar", label: "Solar potential" },
    { key: "environmentalRisks", label: "Radon, mining and other risks" },
    { key: "soldPrices", label: "Sold prices" },
    { key: "comparables", label: "Comparable sales" },
    { key: "broadband", label: "Broadband and mobile" },
    { key: "schools", label: "Schools" },
    { key: "shops", label: "Supermarkets and shops" },
    { key: "greenSpaces", label: "Parks and green spaces" },
    { key: "stations", label: "Stations" },
    { key: "buses", label: "Buses" },
    { key: "crime", label: "Crime" },
    { key: "planning", label: "Planning and flood" },
    { key: "licensing", label: "Letting it out" },
];

/*
 * The map view. Every nearby panel is a list of places with coordinates, so
 * the same data reads as pins around the postcode without a second lookup.
 *
 * One colour per category, matching nothing in particular except itself - the
 * legend is on screen beside the map, so the colours only have to be told
 * apart, not remembered.
 */
/*
 * How far "nearby" reaches, per category, mirroring NEARBY_MILES in
 * api/routes/propertyData.js. Here it draws the rings and sets the zoom; a map
 * whose circles disagreed with the lists beneath them would be worse than
 * drawing no circle at all.
 *
 * One number per category rather than one for all five, because a corner shop
 * and a mainline station are not a walk of the same length - see the note on
 * the API side for the counts that settled each one.
 */
const NEARBY_MILES = {
    schools: 0.7,
    greenSpaces: 0.7,
    shops: 0.4,
    stations: 1,
    buses: 0.4,
};
const metresFor = (category) => Math.round(NEARBY_MILES[category] * 1609.344);

const PIN_COLOURS = {
    schools: "#dfa03e",
    shops: "#0072CE",
    greenSpaces: "#3f9c46",
    stations: "#14315B",
    // A London bus is red, and nobody has to be told that. A colour is not
    // a logo, so this borrows the association without borrowing a mark.
    buses: "#DC241F",
};
const PIN_LABELS = {
    schools: "Schools",
    shops: "Shops",
    greenSpaces: "Parks",
    stations: "Stations",
    buses: "Bus stops",
};

/*
 * A glyph per category, drawn on the pin and repeated in the key.
 *
 * Colour alone was doing all the work, which fails twice: about one man in
 * twelve cannot reliably separate the red shops from the green parks, and
 * nobody holds five colour meanings in their head while scanning a map.
 * A symbol is legible at a glance and survives being printed in mono.
 *
 * Material Icons path data, 24x24, Apache 2.0 - the same set @material-ui
 * already ships in this project, so these are the marks used elsewhere on
 * the site rather than a second icon language.
 */
const CATEGORY_GLYPHS = {
    // mortarboard
    schools: "M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z",
    // shopping trolley
    shops:
        "M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2H1zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2z",
    // tree
    greenSpaces: "M17 12h2L12 2 5.99 12H8l-4 6h7v4h2v-4h7l-4-6z",
    // train
    stations:
        "M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
    // bus
    buses:
        "M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z",
};

/*
 * Stations are split by mode. "Stations" as one pin treats a tram stop and a
 * mainline terminus as the same thing, which is not how anyone weighs up an
 * address - and the report already knows the difference, because TfL return
 * the modes with every stop.
 *
 * DELIBERATELY NOT THE OPERATORS' OWN MARKS. The roundel, the Overground and
 * Elizabeth line marks and the National Rail double arrow are registered
 * trademarks, not open data. TfL's design and branding terms for open data
 * users say not to "use or adapt our branding" and not to "use our
 * trademarks"; the double arrow belongs to the Secretary of State for
 * Transport, and its use is set by the Rail Delivery Group's identity
 * guidelines. Drawing them here would be the same mistake as putting the ICO
 * logo in the footer, which we did not do either.
 *
 * What the same terms DO allow is the words: TfL permit "London Underground",
 * "Tube", "DLR" and "London Overground" to label services in a travel tool.
 * So the names below are theirs and the marks are ours - Material Icons,
 * Apache 2.0, the set already in this project, picked to be told apart at
 * 30px rather than to resemble anybody's logo.
 *
 * Six modes and only five distinct train shapes in the set, so two of them
 * use the OUTLINED cut rather than a sixth near-identical silhouette. Hollow
 * against solid separates at a glance where one more arrangement of little
 * square windows would not - which was the trouble with the first attempt,
 * where the Overground and the Elizabeth line ended up drawing the same mark
 * under two different names.
 */
const MODE_GLYPHS = {
    // subway - a train in a tunnel mouth
    tube: "M7.01 9h10v5h-10zM17.8 2.8C16 2.09 13.86 2 12 2c-1.86 0-4 .09-5.8.8C3.53 3.84 2 6.05 2 8.86V22h20V8.86c0-2.81-1.53-5.02-4.2-6.06zm.2 13.08c0 1.45-1.18 2.62-2.63 2.62l1.13 1.12V20H15l-1.5-1.5h-2.83L9.17 20H7.5v-.38l1.12-1.12C7.18 18.5 6 17.32 6 15.88V9c0-2.63 3-3 6-3 3.32 0 6 .38 6 3v6.88z",
    // tram - pantograph and a single window band
    tram: "M19 16.94V8.5c0-2.79-2.61-3.4-6.01-3.49l.76-1.51H17V2H7v1.5h4.75l-.76 1.52C7.86 5.11 5 5.73 5 8.5v8.44c0 1.45 1.19 2.66 2.59 2.97L6 21.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 20h-.08c1.69 0 2.58-1.37 2.58-3.06zm-7 1.56c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm5-4.5H7V9h10v5z",
    // two square windows and two lamps, for the light rail
    dlr: "M12 2c-4.42 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-6H6V6h5v5zm5.5 6c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6h-5V6h5v5z",
    // a stepped roof, for the longer-distance services
    overground: "M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
    // hollow, so it does not read as the Overground at pin size
    "elizabeth-line": "M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2l2-2h4l2 2h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-4-4-8-4zm0 2c3.51 0 4.96.48 5.57 1H6.43c.61-.52 2.06-1 5.57-1zM6 7h5v3H6V7zm12 8.5c0 .83-.67 1.5-1.5 1.5h-9c-.83 0-1.5-.67-1.5-1.5V12h12v3.5zm0-5.5h-5V7h5v3z",
    // hollow with a single lamp, the plainest of the set, for everything else
    "national-rail": "M12 1c-4.42 0-8 .5-8 4v10.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h12v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V5c0-3.5-3.58-4-8-4zm0 2c6 0 6 1.2 6 2H6c0-.8 0-2 6-2zm6 4v3H6V7h12zm-1.5 10h-9c-.83 0-1.5-.67-1.5-1.5V12h12v3.5c0 .83-.67 1.5-1.5 1.5zM12 12.5c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z",
};
const MODE_LABELS = {
    tube: "Underground",
    "elizabeth-line": "Elizabeth line",
    overground: "Overground",
    dlr: "DLR",
    tram: "Tram",
    "national-rail": "National Rail",
};
/*
 * A stop can carry several modes - Stratford is six of them - so one has to
 * win the pin. Ordered by what makes an address distinctive rather than by
 * passenger numbers: a tube station is the headline, and "national rail" is
 * the fallback because almost everything else also has it.
 */
const MODE_ORDER = ["tube", "elizabeth-line", "overground", "dlr", "tram", "national-rail"];
const stationKind = (modes) =>
    "stations:" + (MODE_ORDER.find((m) => (modes || []).indexOf(m) !== -1) || "national-rail");

/* The five categories that can be plotted, in the order the key lists them. */
const MAP_CATEGORIES = ["schools", "shops", "greenSpaces", "stations", "buses"];

/* A kind is a category, or a category and a mode. These read either. */
const kindCategory = (kind) => kind.split(":")[0];
const kindColour = (kind) => PIN_COLOURS[kindCategory(kind)];
const kindGlyph = (kind) => {
    const parts = kind.split(":");
    return parts[1] ? MODE_GLYPHS[parts[1]] : CATEGORY_GLYPHS[parts[0]];
};
const kindLabel = (kind) => {
    const parts = kind.split(":");
    return parts[1] ? MODE_LABELS[parts[1]] : PIN_LABELS[parts[0]];
};

/*
 * The pin as a data URI, so the map and the key draw the identical mark from
 * one definition. Google's SymbolPath cannot composite a glyph onto a disc,
 * and a bare glyph loses to the map detail underneath it - hence a coloured
 * disc with a white ring, the glyph knocked out of it.
 */
function pinSvg(kind) {
    return (
        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 30 30" width="30" height="30">' +
        '<circle cx="15" cy="15" r="13" fill="' + kindColour(kind) + '" stroke="#ffffff" stroke-width="2.5"/>' +
        '<g transform="translate(6 6) scale(0.75)" fill="#ffffff">' +
        '<path d="' + kindGlyph(kind) + '"/>' +
        "</g></svg>"
    );
}
const pinUrl = (kind) => "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(pinSvg(kind));

/*
 * The key's copy of a pin. Same colours and same path data as pinSvg above,
 * so the mark beside a label is the mark on the map; drawn as JSX rather than
 * a data URI only because it sits in the document rather than in Google's
 * marker layer.
 */
function Mark({ kind, size }) {
    return (
        <svg viewBox="0 0 30 30" width={size || 20} height={size || 20} aria-hidden="true" focusable="false">
            <circle cx="15" cy="15" r="13" fill={kindColour(kind)} stroke="#ffffff" strokeWidth="2.5" />
            <g transform="translate(6 6) scale(0.75)" fill="#ffffff">
                <path d={kindGlyph(kind)} />
            </g>
        </svg>
    );
}

const mapContainerStyle = { width: "100%", height: "100%" };

/*
 * The map lines up with the rest of the report rather than bleeding past it:
 * it sits inside the same <Container> as the "select data options" box and
 * the panels, so all three share one left and right edge. Full-bleed was
 * tried first and looked detached from the page at any width where the
 * container did not already fill the window.
 *
 * Never `width: 100vw; margin-left: calc(50% - 50vw)` if it goes back to
 * full bleed. #root is the scroll container on this site, so 100vw counts the
 * scrollbar and the page gains a horizontal scroll on desktop while looking
 * perfect on a phone - the sort of thing a mobile-only check waves through.
 *
 * SQUARE, not a letterbox. The thing being shown is a circle - everything
 * within 0.7 miles - and a circle in a wide frame is limited by the short
 * side, so a 1400x600 map spends most of its width on land outside the
 * radius while the circle itself stays small. At 1:1 the circle can fill the
 * frame, which is the same map at roughly twice the scale.
 *
 * A square this wide is taller than a laptop window - at 1440x900 the
 * container is 1306px, so the map runs to about 1.45 screens and the ring has
 * to be scrolled through. That is a deliberate trade: an 80vh ceiling was
 * tried and the resulting 720px map read as shrunken against the full-width
 * box above it. Matching the page won.
 *
 * The floor for browsers too old for aspect-ratio - without which the frame
 * collapses to nothing and takes the map with it - is behind @supports on
 * purpose. A plain min-height does not just set a height: with aspect-ratio
 * live, the ratio is honoured from whichever axis is larger, so a 360px floor
 * drove the WIDTH to 360 as well. On a phone that is wider than the 339px
 * container, and the map hung 21px past the box it is meant to line up with
 * while every wider screen looked perfect. max-width is the second guard, so
 * nothing can push the frame past its container again.
 */
const MapFrame = styled.div`
    aspect-ratio: 1 / 1;
    max-width: 100%;
    @supports not (aspect-ratio: 1 / 1) {
        min-height: 20rem;
    }
    /* The same card as the panels above and below it - see BOX_BORDER.
       overflow: hidden is what rounds the map's own corners with the frame,
       and is load-bearing for print as well (see the @media print block). */
    border: ${BOX_BORDER};
    border-radius: ${BOX_RADIUS};
    box-shadow: ${BOX_SHADOW};
    overflow: hidden;
    margin-bottom: 0.9rem;
`;
/*
 * The key under the map is also a control - ticking a row puts that kind of
 * thing on the map, exactly like the "select data options" box above. It is
 * where someone is actually looking when they decide there are too many pins,
 * so it is the natural place to turn one off.
 *
 * Which means it has to list EVERY mappable category, not only the ones being
 * plotted. A key that shows what is on the map is a key; a key that shows only
 * what is on the map, with tick boxes, is a control you can switch off and
 * never back on.
 */
const MapLegend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1.25rem;
    margin-bottom: 1.5rem;
    font-size: 0.85rem;
    color: ${(props) => props.theme.grey};
    @media print {
        /* A key on paper, not a control: nothing here can be ticked. */
        input {
            display: none;
        }
    }
`;
const LegendItem = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
    cursor: pointer;
    user-select: none;
    /* Off reads as off without disappearing - it still has to be findable to
       be ticked back on. Printed keys are never dimmed: see the print block,
       which forces full opacity because only plotted rows survive to paper. */
    opacity: ${(props) => (props.on ? 1 : 0.45)};
    color: ${(props) => props.theme.black};
    svg {
        flex: none;
    }
    input {
        margin: 0;
        width: 1rem;
        height: 1rem;
        accent-color: ${(props) => props.theme.darkblue};
        cursor: pointer;
        flex: none;
    }
    &:hover {
        opacity: 1;
    }
`;
/* The modes inside the Stations row - a mark and its name, repeated. */
const LegendMode = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    white-space: nowrap;
`;

/*
 * List or map. A segmented pair rather than a checkbox: it is one or other.
 *
 * Sized up from a 0.88rem pill with a hairline grey border, which was easy to
 * miss entirely - people were reading the map as the only view, or never
 * finding it. It now carries the brand blue on its border and its active half,
 * and is labelled, so it reads as the switch it is.
 */
/*
 * Both switches on one line. They do the same kind of job - they change how
 * this page is laid out, not what is in it - so sitting them side by side says
 * that, where stacking them read as two unrelated settings that happened to
 * follow each other.
 *
 * It wraps, and has to: one switch is about 310px wide and a phone gives the
 * container 339px, so below roughly 700px they go back to being two rows. The
 * row gap is the smaller of the two for that case.
 *
 * No bottom margin: these sit in the foot of the options card, whose own
 * padding and margin set the distance to the panels below.
 */
const SwitchRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.9rem 1.9rem;
    @media print {
        display: none;
    }
`;
/*
 * Label beside the toggle, until there is not room for both. Inside the
 * options card a phone gives 294px of content width - the card's padding takes
 * 45 of the 339 the page has - and a toggle is 238px with "SHOW AS" and its
 * gap adding another 74. Stacking the label above the toggle below 560px costs
 * one line and is the difference between fitting and overflowing the card.
 */
const ViewSwitch = styled.div`
    display: flex;
    align-items: center;
    gap: 0.6rem;
    @media (max-width: 560px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.35rem;
    }
    @media print {
        display: none;
    }
`;
const ViewLabel = styled.span`
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    color: ${(props) => props.theme.grey};
`;
const ViewToggle = styled.div`
    display: inline-flex;
    border: 2px solid ${(props) => props.theme.darkblue};
    border-radius: 999px;
    overflow: hidden;
    background: #fff;
`;
/*
 * min-width, not padding, sets the half-width. Padding sizes each button to
 * its own label, so "Stacked / Tiles" came out 38px wider than "List / Map"
 * and the two switches did not line up. A floor every label clears makes all
 * four halves identical and therefore both switches identical, whatever the
 * words are.
 *
 * box-sizing is stated rather than assumed: the floor has to include the
 * padding or the widest label pushes past it and the matching breaks again.
 */
const ViewButton = styled.button`
    box-sizing: border-box;
    min-width: 6.5rem;
    padding: 0.55rem 1.1rem;
    font: inherit;
    font-size: 0.95rem;
    font-weight: ${(props) => (props.active ? 700 : 500)};
    border: 0;
    cursor: pointer;
    transition: background 0.14s ease, color 0.14s ease;
    color: ${(props) => (props.active ? "#fff" : props.theme.darkblue)};
    background: ${(props) => (props.active ? props.theme.darkblue : "#fff")};
    &:hover:not(:disabled) {
        background: ${(props) => (props.active ? props.theme.darkblue : "#eef2f7")};
    }
`;

const InfoBody = styled.div`
    max-width: 15rem;
    font-size: 0.85rem;
    color: #121212;
    strong {
        display: block;
        font-size: 0.9rem;
        margin-bottom: 0.15rem;
    }
    span {
        color: #808080;
    }
`;

const SECTIONS_KEY = "pp.property-report.sections";
const LAYOUT_KEY = "pp.property-report.layout";

/*
 * Stacked or tiles, kept per viewer like the section choice and for the same
 * reason: somebody who prefers one will prefer it next time. Its own key
 * rather than a field on the sections record, so a stale or unreadable value
 * for one cannot take the other down with it.
 */
function readStoredLayout() {
    try {
        return window.localStorage.getItem(LAYOUT_KEY) === "tiles" ? "tiles" : "stacked";
    } catch (err) {
        return "stacked";
    }
}

function writeStoredLayout(value) {
    try {
        window.localStorage.setItem(LAYOUT_KEY, value);
    } catch (err) {
        /* Private window, or blocked. The choice simply does not persist. */
    }
}

/*
 * What a first-time visitor sees ticked. Council tax and the energy
 * certificate are the two things people actually arrive for, and an empty
 * form that returns a single box about wards and constituencies does not
 * show what the page is for.
 *
 * Kept deliberately short. Ticking everything would be the other way to
 * avoid an empty page, but it makes the first report a very long scroll and
 * fires every lookup - EPC, TfL, the FSA - at someone who has not asked for
 * any of it.
 */
const DEFAULT_SECTIONS = ["councilTax", "epc"];

/*
 * Remembering the selection is a per-viewer convenience, so it lives in
 * localStorage and nowhere else: it never reaches our server, it never leaves
 * the browser it was set in, and nothing on the page depends on it working.
 *
 * Every access is wrapped. localStorage is not always there to be read - a
 * private window, blocked site data, or a browser that throws on access
 * rather than returning null - and the page has to open sensibly in that
 * case rather than failing. Unreadable is treated as a first visit, which is
 * what it looks like from here.
 *
 * NOTHING STORED IS NOT THE SAME AS AN EMPTY SELECTION, and the difference
 * matters: someone who has never been here gets the defaults, while someone
 * who deliberately cleared every box gets their empty list back rather than
 * having the defaults quietly put back each time they return. So the absence
 * of the key is what triggers the default - a stored "[]" is an answer and is
 * honoured as one.
 *
 * Unknown keys are dropped on the way in. A stored selection outlives a
 * release, so a panel that is later renamed or removed would otherwise sit in
 * someone's list for ever, asking the API for a section it no longer serves.
 * A list that had ONLY unknown keys is left empty rather than defaulted: it
 * was a real choice, just one about sections that no longer exist.
 */
/*
 * The option list as it stood before this store began recording what was on
 * offer. A stored array in the old shape was a decision about these nine keys
 * and about nothing else, so anything outside this list is newer than the
 * visitor's last choice.
 */
const LEGACY_OFFERED = [
    "councilTax",
    "epc",
    "soldPrices",
    "broadband",
    "schools",
    "shops",
    "greenSpaces",
    "stations",
    "buses",
];

/*
 * A SAVED CHOICE IS ABOUT THE OPTIONS THAT EXISTED WHEN IT WAS MADE.
 *
 * Remembering the selection and then adding a panel meant every returning
 * visitor was the last to see it: their stored list had no "crime" in it, so
 * crime stayed off, and the only people who ever saw a new panel were the ones
 * who had never used the page. That is backwards, and it is invisible - the
 * page looks like it simply does not have the feature.
 *
 * So the store now keeps what was OFFERED alongside what was CHOSEN. A key
 * that is in the option list today but was not on offer when they last chose
 * is one they have never had an opinion about, and it starts on. A key they
 * were offered and left unticked stays off, which is the whole point of
 * remembering.
 *
 * One exception, and it is deliberate. An empty selection is not "no opinion",
 * it is "nothing, thank you" - somebody pressed Clear all. Adding a panel back
 * into an empty report would undo exactly the thing they asked for, so an
 * empty list stays empty however many panels ship later.
 */
function readStoredSections() {
    try {
        const raw = window.localStorage.getItem(SECTIONS_KEY);
        if (raw === null || raw === "") return DEFAULT_SECTIONS.slice();

        const known = DATA_OPTIONS.map((o) => o.key);
        const parsed = JSON.parse(raw);

        // Two shapes: the bare array this started as, and the record that
        // replaced it. Both are read, so an old browser is not reset.
        const chosen = Array.isArray(parsed)
            ? parsed
            : parsed && Array.isArray(parsed.sections)
            ? parsed.sections
            : null;
        if (!chosen) return DEFAULT_SECTIONS.slice();

        const offered = Array.isArray(parsed) || !Array.isArray(parsed.offered)
            ? LEGACY_OFFERED
            : parsed.offered;

        const picked = chosen.filter((k) => known.indexOf(k) !== -1);
        if (picked.length === 0) return []; // Clear all means clear all.

        const unseen = known.filter((k) => offered.indexOf(k) === -1 && picked.indexOf(k) === -1);
        return picked.concat(unseen);
    } catch (err) {
        return DEFAULT_SECTIONS.slice();
    }
}

function writeStoredSections(list) {
    try {
        window.localStorage.setItem(
            SECTIONS_KEY,
            // What was on offer travels with what was chosen, so the next
            // release can tell a deliberate "no" from a panel that did not
            // exist yet without having to hard-code another legacy list.
            JSON.stringify({ sections: list, offered: DATA_OPTIONS.map((o) => o.key) })
        );
    } catch (err) {
        /* Full, blocked, or a private window. The page works without it. */
    }
}

const OptionsBar = styled.div`
    margin: ${(props) => (props.light ? "0 0 1.5rem" : "1.25rem 0 0")};
    padding: ${(props) => (props.light ? "1rem 1.25rem" : "0")};
    border: ${(props) => (props.light ? BOX_BORDER : "0")};
    border-radius: ${(props) => (props.light ? BOX_RADIUS : "0")};
    background: ${(props) => (props.light ? "#fff" : "transparent")};
    /* Only the light copy is a card. The one in the hero sits on the dark
       band and would cast a shadow onto navy, which shows as a dirty smear. */
    box-shadow: ${(props) => (props.light ? BOX_SHADOW : "none")};
    color: ${(props) => (props.light ? props.theme.black : "#fff")};
    @media print {
        display: none;
    }
`;
const OptionsLabel = styled.div`
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 600;
    opacity: ${(props) => (props.light ? 1 : 0.75)};
    color: ${(props) => (props.light ? props.theme.grey : "#fff")};
    margin-bottom: 0.5rem;
`;
const OptionsList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 1.1rem;
    align-items: center;
`;
const OptionLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.88rem;
    cursor: pointer;
    user-select: none;
    input {
        width: 1rem;
        height: 1rem;
        margin: 0;
        cursor: pointer;
        accent-color: ${(props) => props.theme.darkblue};
    }
`;
/*
 * The foot of the card, for controls that belong with the options but are not
 * options. A rule rather than a gap: "Select all" is the last thing in the
 * list above and is itself a bare underlined control, so spacing alone would
 * read as one more item in that row.
 */
const OptionsExtra = styled.div`
    margin-top: 0.9rem;
    padding-top: 0.9rem;
    border-top: ${BOX_BORDER};
`;
const OptionsAction = styled.button`
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    font-size: 0.8rem;
    color: inherit;
    opacity: 0.8;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
        opacity: 1;
    }
`;

/*
 * Rendered twice: once in the hero above the postcode box, and once under the
 * location panel so the choice can be changed without scrolling back up. Both
 * drive the same state, so ticking a box in either place moves both.
 *
 * Children go in a ruled-off foot. Only the light copy is given any: the hero
 * copy is on screen before a postcode has been entered, and a switch between
 * two views of a report that does not exist yet has nothing to switch.
 */
function DataOptions({ light, selected, onToggle, onAll, onNone, children }) {
    const all = selected.length === DATA_OPTIONS.length;
    return (
        <OptionsBar light={light}>
            <OptionsLabel light={light}>Select data options</OptionsLabel>
            <OptionsList>
                {DATA_OPTIONS.map((o) => (
                    <OptionLabel key={o.key}>
                        <input
                            type="checkbox"
                            checked={selected.indexOf(o.key) !== -1}
                            onChange={() => onToggle(o.key)}
                        />
                        {o.label}
                    </OptionLabel>
                ))}
                <OptionsAction type="button" onClick={all ? onNone : onAll}>
                    {all ? "Clear all" : "Select all"}
                </OptionsAction>
            </OptionsList>
            {children && <OptionsExtra>{children}</OptionsExtra>}
        </OptionsBar>
    );
}

/*
 * A distance someone can picture. Under a tenth of a mile, miles are the
 * wrong unit twice over: "0.1 mi" is vague, and anything under about 80
 * metres rounds to "0 mi", which reads as no distance at all. A park whose
 * boundary is three metres away was showing as "0 mi" when what it is, is
 * across the road. Zero is reserved for genuinely inside.
 */
/*
 * "1 miles" is what you get from gluing a number to a word. Stations sit at
 * exactly 1 mile, so the only radius that needed this is the one that had it
 * wrong on the live page.
 */
const milesLabel = (miles) => miles + (miles === 1 ? " mile" : " miles");

function distanceLabel(metres, miles) {
    if (metres === 0) return "adjacent";
    if (metres < 160) return metres + " m";
    return miles + " mi";
}

function bandFor(score) {
    const hit = EPC_BANDS.find((b) => score >= b[1] && score <= b[2]);
    return hit ? hit[0] : null;
}

/*
 * REQUIRED, not decorative. The EPC licence permits publishing address data
 * for its listed purposes only on condition that this notice travels "with
 * the data". Quoted verbatim from MHCLG's licensing restrictions page - do
 * not reword it.
 *
 * One copy, rendered twice: beside the address list on screen, and again at
 * the foot of the report on paper, because printing hides the hero and a
 * saved PDF is exactly the case where the notice could be separated.
 */
function AddressLicence() {
    return (
        <>
            This data contains address data (address 1, address 2, address 3, postcode &ndash; processed against
            Ordnance Survey&rsquo;s Address Base Premium product, which incorporates Royal Mail&rsquo;s PAF&reg;
            database (Address Data). MHCLG has published the address data with the consent of Ordnance Survey and
            Royal Mail Group Limited; Ordnance Survey and Royal Mail Group Limited reserve all their respective
            copyright, database rights, trademarks and other intellectual property rights. All other fields are
            licensed under the{" "}
            <a
                href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
                target="_blank"
                rel="noopener noreferrer"
            >
                Open Government Licence v3.0
            </a>
            .
        </>
    );
}

export default function PropertyReport() {
    useLayoutEffect(() => {
        document.getElementById("root").scrollTo(0, 0);
    }, []);
    /*
     * One page, two brands. The report tool is the same tool on both domains -
     * same data, same API, same panels - so it is one component rather than
     * two. What differs is the chrome around it, the name on the PDF, and the
     * title in a search result.
     *
     * Read at render rather than at module load: a static import would be
     * evaluated once and the server-rendered copy has no window at all.
     */
    const brand = brandForHost(typeof window === "undefined" ? "" : window.location.hostname);
    const brandHost = brand.origin.replace(/^https?:\/\//, "");

    useStaticSeo(
        brand.key === "product" ? PRODUCT_SEO["/property-report"] : STATIC_SEO["/property-report"]
    );

    /*
     * A postcode handed over from the homepage's search box, which navigates
     * to /property-report?postcode=... rather than running its own copy of
     * this lookup. Seeded into state here and run once on mount below.
     *
     * Read once at first render rather than watched: this is a starting
     * value, not a bound parameter. The URL is not kept in step afterwards -
     * a report's state still does not reach the address bar, which is the
     * reason this page cannot be indexed and is noted in the spec.
     */
    const initialPostcode =
        typeof window === "undefined"
            ? ""
            : new URLSearchParams(window.location.search).get("postcode") || "";

    /*
     * The certificate a shared link names, so an emailed report opens on the
     * property it was about rather than on its postcode's address list. Read
     * once on arrival for the same reason as the postcode above, and acted on
     * only after the lookup has returned the address list it has to match
     * against - see the arrival effect below.
     */
    const initialCertificate =
        typeof window === "undefined"
            ? ""
            : new URLSearchParams(window.location.search).get("certificate") || "";

    /*
     * A valuation that was produced earlier, named by a shared link.
     *
     * WHY A LINK CARRIES ONE AT ALL. Until 31 October 2026 the valuation
     * lived in this tab's state and nowhere else, so a shared report arrived
     * without the figure the sender had just paid a model to produce - and a
     * refresh lost it just as completely. The token names a stored valuation
     * (api/models/Valuation.js); the panel fetches it instead of asking
     * again.
     *
     * Shape-checked here rather than trusted to the API, so a mangled link
     * is ignored quietly instead of producing an error on a page that is
     * otherwise fine.
     */
    const initialValuation = valuationTokenFromUrl();

    /*
     * A short link carries ONLY the token, so the postcode and certificate
     * have to be fetched before anything else can happen. True while that
     * is in flight, so the page says it is opening a shared report rather
     * than showing an empty search box for a second.
     */
    const arrivedShort = hasShortLink() && !initialPostcode;

    /*
     * Lifted out of the valuation panel so the share buttons can see it.
     * The panel owns producing it; this page owns what the link says.
     */
    const [valuationToken, setValuationToken] = useState(initialValuation);

    /*
     * The certificate the page should open on. Seeded from the long link,
     * and filled in from the token when the link was the short kind.
     */
    const [arrivalCert, setArrivalCert] = useState(initialCertificate);

    /* Set when a shared link names a valuation we cannot resolve. */
    const [sharedLinkProblem, setSharedLinkProblem] = useState(false);
    const [openingShared, setOpeningShared] = useState(arrivedShort);

    /* Whether the email form beside the address is open. Lifted so the
       valuation panel's "Email it" can open it. */
    const [emailOpen, setEmailOpen] = useState(false);

    const [postcode, setPostcode] = useState(initialPostcode);
    const [report, setReport] = useState(null);
    const [band, setBand] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [chosen, setChosen] = useState(null);
    const [certLoading, setCertLoading] = useState(false);
    const [selectedCert, setSelectedCert] = useState("");
    // Once a property is chosen the panels narrow to it. These let the
    // visitor widen them back out to the whole postcode.
    const [showAllBands, setShowAllBands] = useState(false);
    const [showAllSales, setShowAllSales] = useState(false);
    // A dense postcode can sit inside 20+ schools and 50+ bus stands, which
    // buries the rest of the report. Both lists open at the nearest few.
    const [showAllSchools, setShowAllSchools] = useState(false);
    const [showAllStops, setShowAllStops] = useState(false);
    const [showAllGreen, setShowAllGreen] = useState(false);
    const [showAllShops, setShowAllShops] = useState(false);
    const [showAllCrime, setShowAllCrime] = useState(false);
    const [showAllComps, setShowAllComps] = useState(false);
    const [layout, setLayout] = useState(readStoredLayout);
    /*
     * Which panels the visitor has asked for. Empty means the location facts
     * alone, which is how the page opens for a first-time visitor - a
     * returning one gets whatever they last chose, read synchronously here so
     * the boxes are right on the first paint rather than ticking themselves a
     * moment later.
     */
    const [selected, setSelected] = useState(readStoredSections);
    const [view, setView] = useState("list");
    const tiles = layout === "tiles";
    const [openPin, setOpenPin] = useState(null);

    /*
     * The Maps script is 300 KB and the report does not need it, so the hook
     * is told not to load until someone actually opens the map. Every marker
     * icon below is built with `new window.google.maps.Symbol...`, which
     * throws if the API is not there yet - see the note in useGoogleMaps.js
     * about why that blanks the page rather than failing quietly.
     */
    const mapsReady = useGoogleMaps(view === "map");
    // Set by a "Share as PDF" button when the report it wants is still
    // loading. The effect below prints once it has arrived.
    const [printPending, setPrintPending] = useState(false);
    // Guards the one-shot auto-pick of a certificate named in a shared link.
    const [autoPicked, setAutoPicked] = useState(false);

    const api = axios.create({ baseURL: process.env.REACT_APP_API_URL });

    /*
     * One option per property, in address order. The API returns every
     * certificate newest-first, so a home assessed twice appears twice; keep
     * the first (newest) per UPRN, falling back to the address text when a
     * certificate has no UPRN. numeric:true sorts "2" before "10".
     *
     * Each option keeps its EPC band. That is deliberate: the decision to
     * publish EPC address data rests on the list never being a bare address
     * list - see the licensing section of the build spec.
     */
    const epcList =
        report && report.epc && report.epc.status === "found" ? report.epc.certificates || [] : [];
    const seen = new Set();
    const addressOptions = epcList
        .filter((c) => {
            const key = c.uprn || c.address;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        })
        .sort((a, b) => a.address.localeCompare(b.address, "en-GB", { numeric: true, sensitivity: "base" }));
    const hasAddresses = addressOptions.length > 0;
    // Told apart deliberately: "none registered" is an answer, "the lookup
    // failed" is not knowing. See the address placeholder below.
    const epcFailed = !!(report && report.epc && report.epc.status === "unavailable");

    /*
     * The postcode a share link should carry: the one the API normalised if a
     * lookup has happened, otherwise whatever is in the box.
     *
     * HOISTED OUT OF THE JSX ON PURPOSE, and it has to stay out. Written
     * inline as `postcode={report ? report.postcode : postcode}` inside the
     * `{brand.key === "product" && (...)}` gate below, it makes the
     * eslint-plugin-react-hooks that react-scripts 4 pins report
     * "React Hook useEffect is called conditionally" against all SEVEN
     * useEffects in this component - none of which are conditional. A
     * conditional expression nested inside a logical-&& JSX gate throws off
     * its control-flow analysis, and the error points at the hooks rather
     * than at the JSX, which makes it a genuinely nasty half hour. The build
     * treats it as an error, not a warning, so it does not ship either way.
     */
    const sharePostcode = report ? report.postcode : postcode;

    /*
     * Whether the valuation panel is on the page, and therefore whether
     * there is anything to scroll to.
     *
     * HOISTED FOR THE SAME REASON AS sharePostcode ABOVE, and it has to
     * stay hoisted. Written inline as
     * `{brand.key === "product" && chosen && (...)}` inside the address row,
     * it makes the pinned eslint-plugin-react-hooks report
     * "React Hook useEffect is called conditionally" against every useEffect
     * in this component. The identical expression is harmless further down
     * the file where the panel itself is gated, which is what makes this so
     * unpleasant to track down - it is the position, not the expression.
     */
    const canValue = brand.key === "product" && Boolean(chosen);

    /*
     * Scrolling helpers.
     *
     * DECLARED HERE, BELOW EVERY HOOK, AND THEY HAVE TO STAY BELOW. Written
     * among the useState calls above, these two function declarations made
     * the eslint-plugin-react-hooks that react-scripts 4 pins report
     * "React Hook useEffect is called conditionally" against all EIGHT
     * useEffects in this component - none of which are conditional, and
     * none of which moved. It is the same control-flow confusion the note
     * on sharePostcode above describes, and it costs the same half hour,
     * because the error points at the hooks rather than at the statement
     * that upset it. The build treats it as an error, so it does not ship.
     *
     * `smooth` unless the reader has asked for less motion - this is a very
     * long page, and a smooth scroll down fifteen sections is exactly what
     * prefers-reduced-motion exists for.
     */
    function scrollToAnchor(id, block) {
        const target = document.getElementById(id);
        if (!target) return;
        const still =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        target.scrollIntoView({ behavior: still ? "auto" : "smooth", block: block });
    }

    function goToValuation() {
        scrollToAnchor(VALUATION_ANCHOR, "start");
    }

    function openEmailForm() {
        setEmailOpen(true);
        scrollToAnchor(ACTIONS_ANCHOR, "center");
    }

    async function run(value) {
        const pc = String(value || "").trim();
        if (!pc) return;

        setLoading(true);
        setError(null);
        setBand(null);
        setChosen(null);
        setSelectedCert("");
        setShowAllBands(false);
        setShowAllSales(false);

        try {
            const res = await api.get("/property-data/report", {
                // Always sent, even empty: an absent sections means "all" to
                // the API, which is the opposite of an untouched page.
                params: { postcode: pc, sections: selected.join(",") },
            });
            setReport(res.data);
        } catch (err) {
            const data = err.response && err.response.data;
            setReport(null);
            setError((data && (data.detail || data.error)) || "Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    /*
     * Second step. The address list comes back with the postcode lookup, but
     * the fields the panel actually shows - floor area, costs, potential
     * rating - only exist on the single-certificate endpoint, so picking an
     * address costs one more round trip.
     */
    async function pick(cert) {
        if (!report) return;
        setChosen(cert);
        setCertLoading(true);
        setShowAllBands(false);
        setShowAllSales(false);

        try {
            const res = await api.get("/property-data/report", {
                params: {
                    postcode: report.postcode,
                    certificate: cert.certificateNumber,
                    // Lets the API resolve this property’s council tax band.
                    uprn: cert.uprn || undefined,
                    // Only used to flag probable matches in the sold price
                    // list - Land Registry have no property reference to
                    // join on, so the comparison is on address text.
                    address: cert.address,
                    sections: selected.join(","),
                },
            });
            setReport(res.data);

            /*
             * If the band came back resolved, select it for the visitor
             * rather than asking. They can still change it: the buttons
             * stay live and “show all eight bands” reopens the table.
             */
            const resolved =
                res.data.councilTax &&
                res.data.councilTax.propertyBand &&
                res.data.councilTax.propertyBand.band;
            if (resolved) setBand(resolved);
        } catch (err) {
            const data = err.response && err.response.data;
            setError((data && (data.detail || data.error)) || "Could not load that certificate.");
        } finally {
            setCertLoading(false);
        }
    }

    /*
     * The chat widget cannot be hidden with CSS. Tawk puts
     * `display: block !important` in its container's style attribute and
     * `visibility: visible !important` on each of its four iframes, and an
     * inline declaration outranks every stylesheet - so print rules lose,
     * and the widget lands in the middle of the PDF. Their own API is the
     * only handle on it.
     *
     * Bound to the print events rather than to the buttons so that Ctrl+P
     * gets the same clean page, and restored afterwards either way.
     */
    /*
     * Arriving with a postcode already chosen runs the lookup straight away,
     * so the hand-off from the homepage is one action rather than two. Empty
     * dependency list on purpose: this is an arrival, not something to repeat
     * when the field is later edited by hand.
     */
    useEffect(() => {
        if (initialPostcode) run(initialPostcode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * A short link names only the valuation, so the property has to be
     * looked up before the report can be built. One extra request, and the
     * trade is a URL short enough to survive being emailed - a long one
     * arrived on 31 October 2026 with its token cut to five characters.
     *
     * Failure is SAID OUT LOUD. The first version of this swallowed it, and
     * the result was a shared report that quietly showed an empty valuation
     * form - which reads as the feature not working rather than as a link
     * that did not survive the journey.
     */
    useEffect(() => {
        if (!arrivedShort || !initialValuation) return;

        let cancelled = false;
        fetch(
            (process.env.REACT_APP_API_URL || "") +
                "property-data/valuation/" +
                initialValuation +
                "/where"
        )
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error("gone"))))
            .then((d) => {
                if (cancelled) return;
                if (!d || !d.postcode) throw new Error("gone");
                setArrivalCert(d.certificate || "");
                setPostcode(d.postcode);
                setOpeningShared(false);
                run(d.postcode);
            })
            .catch(() => {
                if (cancelled) return;
                setOpeningShared(false);
                setSharedLinkProblem(true);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /*
     * A shared link names a certificate as well as a postcode, so the report
     * it was sent about is the one that opens. This cannot run with the
     * arrival effect above: the certificate has to be found in the address
     * list, and that list only exists once the postcode lookup has returned.
     *
     * Fires once, guarded on a flag rather than on `chosen` alone, so that a
     * visitor who arrives by link and then picks a DIFFERENT address is not
     * dragged back to the one in the URL on the next render.
     */
    useEffect(() => {
        if (!arrivalCert || autoPicked || chosen) return;
        const cert = addressOptions.find((c) => c.certificateNumber === arrivalCert);
        if (!cert) return;
        setAutoPicked(true);
        setSelectedCert(cert.certificateNumber);
        pick(cert);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressOptions, arrivalCert, autoPicked, chosen]);

    useEffect(() => {
        const call = (name) => {
            try {
                const tawk = window.Tawk_API;
                if (tawk && typeof tawk[name] === "function") tawk[name]();
            } catch (e) {
                // Widget not loaded, or blocked. Nothing to hide either way.
            }
        };
        const hide = () => call("hideWidget");
        const show = () => call("showWidget");
        window.addEventListener("beforeprint", hide);
        window.addEventListener("afterprint", show);
        return () => {
            window.removeEventListener("beforeprint", hide);
            window.removeEventListener("afterprint", show);
        };
    }, []);

    /*
     * Two things that only matter at the moment of printing.
     *
     * THE MAP. Google lays its tiles out for the width the frame had when it
     * drew them and never re-draws for a print stylesheet, so letting print
     * resize the frame leaves the tiles wider than their box and spilling out
     * of it. Scaling is the way round that - it paints the same pixels smaller
     * and triggers no re-layout - but WHAT is scaled matters.
     *
     * Scaling the frame itself was the first attempt and it made the whole
     * report shrink. The frame kept its screen width, 1353px on a desktop, so
     * the printed page was 1353px wide against 794px of paper and Chrome fitted
     * the page to the sheet: everything lost a third of its size, the map twice
     * over, which is why it came out tiny.
     *
     * So the FRAME takes the printed column and the CONTENT inside it is
     * scaled. The page is then the width of the paper, nothing is fitted down,
     * and the map fills its column.
     *
     * 688px is that column: A4 at 96dpi is 794px wide, less the 14mm margins in
     * @page. Letter is wider, so it fits there too with a little white to the
     * right - which is the safe direction to be wrong in.
     *
     * THE FILENAME. Save as PDF takes its suggested name from document.title,
     * which is the SEO title and the same for every report. Pointing it at the
     * address for the length of the print gives a file worth keeping.
     */
    useEffect(() => {
        const PRINT_COLUMN_PX = 688;
        let restoreTitle = null;
        let titleGuard = null;

        const onBefore = () => {
            const frame = document.querySelector(".report-map");
            if (frame) {
                // Google's own container, the thing holding the tiles.
                const inner = frame.firstElementChild;
                const drawn = frame.offsetWidth;
                if (inner && drawn > PRINT_COLUMN_PX) {
                    const scale = PRINT_COLUMN_PX / drawn;
                    // The frame becomes the column, square, and clips.
                    frame.style.width = PRINT_COLUMN_PX + "px";
                    frame.style.height = PRINT_COLUMN_PX + "px";
                    frame.style.maxWidth = "none";
                    frame.style.overflow = "hidden";
                    // The tiles keep the size they were drawn at and are
                    // scaled into it, so nothing is re-laid-out.
                    inner.style.width = drawn + "px";
                    inner.style.height = drawn + "px";
                    inner.style.transformOrigin = "top left";
                    inner.style.transform = "scale(" + scale + ")";
                }
            }

            if (report) {
                restoreTitle = document.title;
                const who = chosen && chosen.address ? chosen.address : report.postcode;
                const wanted = who + " - Property Report";
                document.title = wanted;

                /*
                 * Setting it once is not enough. The chat widget rewrites
                 * document.title to "1 new message" to flash for attention,
                 * on its own schedule - so a print started at the wrong
                 * moment would suggest THAT as the filename. Caught only by
                 * reading the title back during a print rather than after
                 * setting it.
                 *
                 * Watched for the length of the print and put straight back.
                 * Cheaper than asking the widget to behave, and it holds
                 * against anything else that writes to the title too.
                 */
                const titleEl = document.querySelector("title");
                if (titleEl && window.MutationObserver) {
                    titleGuard = new MutationObserver(() => {
                        if (document.title !== wanted) document.title = wanted;
                    });
                    titleGuard.observe(titleEl, { childList: true, characterData: true, subtree: true });
                }
            }
        };

        const onAfter = () => {
            const frame = document.querySelector(".report-map");
            if (frame) {
                const inner = frame.firstElementChild;
                frame.style.width = "";
                frame.style.height = "";
                frame.style.maxWidth = "";
                frame.style.overflow = "";
                if (inner) {
                    inner.style.width = "";
                    inner.style.height = "";
                    inner.style.transform = "";
                    inner.style.transformOrigin = "";
                }
            }
            if (titleGuard) {
                titleGuard.disconnect();
                titleGuard = null;
            }
            if (restoreTitle !== null) {
                document.title = restoreTitle;
                restoreTitle = null;
            }
        };

        window.addEventListener("beforeprint", onBefore);
        window.addEventListener("afterprint", onAfter);
        return () => {
            window.removeEventListener("beforeprint", onBefore);
            window.removeEventListener("afterprint", onAfter);
            onAfter();
        };
    }, [report, chosen]);



    /*
     * Ticking a box once a report is on screen has to go and get that panel's
     * data, because the last request deliberately did not ask for it.
     *
     * Debounced, because someone choosing what they want ticks four boxes in
     * two seconds and each one would otherwise be a request against a 30-a-
     * minute limit. The certificate is carried through, so changing the
     * selection on a property report keeps it a property report rather than
     * dropping back to the postcode.
     */
    const selectionKey = selected.join(",");

    // Kept separate from the refetch below: the choice is worth remembering
    // whether or not a report is on screen when it changes.
    useEffect(() => {
        writeStoredSections(selected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionKey]);

    useEffect(() => {
        if (!report) return undefined;
        const timer = setTimeout(function () {
            const params = { postcode: report.postcode, sections: selectionKey };
            if (chosen) {
                params.certificate = chosen.certificateNumber;
                params.uprn = chosen.uprn || undefined;
                params.address = chosen.address;
            }
            setCertLoading(true);
            api.get("/property-data/report", { params: params })
                .then(function (res) { setReport(res.data); })
                .catch(function () { /* leave what is on screen rather than blanking it */ })
                .then(function () { setCertLoading(false); });
        }, 400);
        return function () { clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectionKey]);

    function toggleOption(key) {
        setSelected(function (prev) {
            return prev.indexOf(key) === -1 ? prev.concat(key) : prev.filter(function (k) { return k !== key; });
        });
    }
    const has = (key) => selected.indexOf(key) !== -1;

    /*
     * A panel whose contents are places is replaced by the map when the map
     * is showing - the pins are the same rows. One with nothing to plot -
     * council tax, energy, sold prices, broadband - stays in either view,
     * because switching to the map should not cost you the council tax band.
     */
    const showPanel = (key) => has(key) && (view === "list" || !PIN_COLOURS[key]);

    /*
     * "Share as PDF" hands the report to the browser's own print dialogue,
     * where every browser offers "Save as PDF" as a destination. No library:
     * adding one changes front/package.json, and deploy.sh then re-resolves
     * the 2021 dependency tree (CLAUDE.md section 4). What gets printed is
     * whatever is on screen, which is also what the visitor just read - the
     * PDF cannot say something the page did not.
     *
     * Waiting is handled rather than blocked: press it with a postcode typed
     * but not yet looked up and the lookup runs first, then the dialogue
     * opens. This effect is the only caller of print(), so there is one
     * place where "is the data actually here yet" gets decided.
     */
    useEffect(() => {
        if (!printPending) return;
        if (loading || certLoading) return; // still fetching; wait for it
        setPrintPending(false);
        if (!report) return; // the lookup failed - the error is on screen
        window.print();
    }, [printPending, loading, certLoading, report]);

    function printPostcodeReport() {
        if (!report && postcode.trim()) run(postcode);
        setPrintPending(true);
    }

    function printPropertyReport() {
        const cert = addressOptions.find((c) => c.certificateNumber === selectedCert);
        // Pick it first if they chose an address but never pressed the button.
        if (cert && (!chosen || chosen.certificateNumber !== selectedCert)) pick(cert);
        setPrintPending(true);
    }

    /*
     * Everything with a position, flattened into one list of pins. Built from
     * exactly the same data the panels render, so the map can never show a
     * place the list does not - or a place the visitor did not ask for.
     *
     * Anything without coordinates is dropped rather than guessed at: a few
     * TfL stops and FSA premises come back ungeocoded, and a pin in the wrong
     * road is worse than a missing one.
     */
    const pins = [];
    if (report) {
        const add = (category, rows, label, kindOf) => {
            if (!has(category) || !rows) return;
            rows.forEach((r) => {
                if (typeof r.lat !== "number" || typeof r.lon !== "number") return;
                pins.push({
                    category,
                    kind: kindOf ? kindOf(r) : category,
                    lat: r.lat,
                    lon: r.lon,
                    name: r.name || label,
                    detail: r.detail,
                    metres: r.metres,
                    miles: r.miles,
                });
            });
        };
        add("schools", report.schools && report.schools.primary);
        add("schools", report.schools && report.schools.secondary);
        add("shops", report.shops && report.shops.supermarkets);
        add("shops", report.shops && report.shops.shops);
        add("greenSpaces", report.greenSpaces && report.greenSpaces.sites, "Green space");
        add("stations", report.transport && report.transport.stations, undefined, (r) =>
            stationKind(r.modes)
        );
        add("buses", report.transport && report.transport.busStops);
    }
    const pinCategories = [...new Set(pins.map((p) => p.category))];
    // The key lists one row per mark actually on the map, so a postcode with
    // a tube station and a tram stop gets both rather than one "Stations".
    const pinKinds = [...new Set(pins.map((p) => p.kind))];

    /*
     * A ring per distinct radius among the categories actually ticked - so a
     * report showing only buses draws one tight circle, not the full set.
     * Widest first, because a later Circle paints over an earlier one and the
     * small rings would otherwise be buried under the big one's fill.
     */
    const ringMetres = [...new Set(selected.filter((k) => NEARBY_MILES[k]).map((k) => metresFor(k)))].sort(
        (a, b) => b - a
    );
    const rings = ringMetres.map((m, i) => ({ metres: m, outermost: i === 0 }));
    const widestRingMetres = ringMetres.length ? ringMetres[0] : metresFor("schools");
    const centre =
        report && report.location && typeof report.location.latitude === "number"
            ? { lat: report.location.latitude, lng: report.location.longitude }
            : null;

    const tax = report && report.councilTax;
    const hasBands = tax && tax.status === "derived";

    return (
        <>
            <PrintStyles />

            <div className="no-print">
                <SiteNav location="public" pageTitle="Property Report" />
            </div>

            <Hero className="no-print" product={brand.key === "product"}>
                <Container>
                    <HeroInner>
                        <H1>Property Data</H1>
                        <Standfirst>Sourced from Gov &amp; other public records.</Standfirst>

                        <DataOptions
                            selected={selected}
                            onToggle={toggleOption}
                            onAll={() => setSelected(DATA_OPTIONS.map((o) => o.key))}
                            onNone={() => setSelected([])}
                        />

                        <SearchBar
                            onSubmit={(e) => {
                                e.preventDefault();
                                run(postcode);
                            }}
                        >
                            <label htmlFor="postcode" className="sr-only" style={{ display: "none" }}>
                                Postcode
                            </label>
                            <PostcodeInput
                                id="postcode"
                                type="text"
                                value={postcode}
                                placeholder="Enter a postcode"
                                autoComplete="postal-code"
                                spellCheck="false"
                                onChange={(e) => setPostcode(e.target.value)}
                            />
                            <Go type="submit" disabled={loading}>
                                {loading ? "Looking up…" : "Postcode Report"}
                            </Go>
                            <PdfButton
                                type="button"
                                disabled={loading || (!report && !postcode.trim())}
                                onClick={printPostcodeReport}
                                title="Opens your browser's print dialogue, where you can choose Save as PDF"
                            >
                                Share as PDF
                            </PdfButton>
                        </SearchBar>

                        {/*
                         * Always on the page, disabled until a postcode has
                         * been looked up. Two lines, both visible from the
                         * start, show what the search actually is: a postcode,
                         * then a property within it. Rendering it only after a
                         * lookup meant the page arrived a line short, grew one
                         * when you searched, and had to fill the gap underneath
                         * with a white "enter a postcode above" box that said
                         * nothing the empty field was not already saying.
                         */}
                        <>
                                {/*
                                  * A shared link that did not survive the
                                  * journey. Said plainly, because the first
                                  * version of this failed silently and showed
                                  * an empty valuation form - which reads as
                                  * the feature being broken rather than the
                                  * link being cut. One arrived with five
                                  * characters of a thirty-two character
                                  * token.
                                  */}
                                {sharedLinkProblem && (
                                    <Reason role="status" style={{ marginBottom: "1rem" }}>
                                        That shared link could not be opened — it may have been
                                        cut short on its way here, or the valuation it names has
                                        since been removed. Enter the postcode below and the
                                        report will build as usual.
                                    </Reason>
                                )}
                                {openingShared && (
                                    <Reason role="status" style={{ marginBottom: "1rem" }}>
                                        Opening the shared report…
                                    </Reason>
                                )}

                                <AddressRow id={ACTIONS_ANCHOR}>
                                    <AddressSelect
                                        id="address"
                                        aria-label="Choose your property"
                                        value={selectedCert}
                                        disabled={!hasAddresses}
                                        onChange={(e) => setSelectedCert(e.target.value)}
                                    >
                                        <option value="">
                                            {hasAddresses
                                                ? "Choose your property (" +
                                                  addressOptions.length +
                                                  " in " +
                                                  report.postcode +
                                                  ")"
                                                : !report
                                                ? "Enter a postcode above to see more"
                                                : /*
                                                   * "No address list" is a claim about the
                                                   * postcode and must only be made when the
                                                   * register actually answered. A failed or
                                                   * timed-out lookup means we do not know,
                                                   * and saying there are none is worse than
                                                   * saying nothing - it sent people away
                                                   * from postcodes that have fifty
                                                   * certificates.
                                                   */
                                                  epcFailed
                                                ? "Address list unavailable — try again"
                                                : "No address list for " + report.postcode}
                                        </option>
                                        {addressOptions.map((c) => (
                                            <option key={c.certificateNumber} value={c.certificateNumber}>
                                                {c.address}
                                                {c.band ? " — EPC " + c.band : ""}
                                            </option>
                                        ))}
                                    </AddressSelect>
                                    <Go
                                        type="button"
                                        disabled={!selectedCert || certLoading}
                                        onClick={() => {
                                            const cert = addressOptions.find(
                                                (c) => c.certificateNumber === selectedCert
                                            );
                                            if (cert) pick(cert);
                                        }}
                                    >
                                        {certLoading ? "Loading…" : "Property Report"}
                                    </Go>
                                    <PdfButton
                                        type="button"
                                        disabled={!selectedCert || certLoading}
                                        onClick={printPropertyReport}
                                        title="Opens your browser's print dialogue, where you can choose Save as PDF"
                                    >
                                        Share as PDF
                                    </PdfButton>
                                    {/*
                                      * The valuation is the last panel on a
                                      * long report, and somebody who came for
                                      * it should not have to scroll past
                                      * fifteen sections to find out it exists.
                                      * Product brand only - the agency does
                                      * not offer it.
                                      */}
                                    {canValue && (
                                        <PdfButton
                                            type="button"
                                            onClick={goToValuation}
                                            title="Jump to the valuation at the foot of the report"
                                        >
                                            Value this property
                                        </PdfButton>
                                    )}
                                    {/* Product brand only, as asked - and the
                                        gate is brand.js, never a hostname read
                                        here (section 6f). */}
                                    {brand.key === "product" && (
                                        <ShareReport
                                            postcode={sharePostcode}
                                            cert={chosen}
                                            brandName={brand.name}
                                            disabled={certLoading}
                                            valuationToken={valuationToken}
                                            open={emailOpen}
                                            onOpenChange={setEmailOpen}
                                        />
                                    )}
                                    {canValue && (
                                        <SaveProperty
                                            postcode={sharePostcode}
                                            cert={chosen}
                                            valuationToken={valuationToken}
                                        />
                                    )}
                                </AddressRow>

                                {/* The licence has to travel WITH the address
                                    data, so it appears only once there is
                                    address data to travel with. */}
                                {hasAddresses && (
                                    <HeroNotice>
                                        <AddressLicence />
                                    </HeroNotice>
                                )}
                        </>

                        {report && addressOptions.length === 0 && report.epc && report.epc.status === "none" && (
                            <HeroNote>
                                No energy certificates are registered in {report.postcode}, so there&rsquo;s no
                                address list. The report below covers the postcode.
                            </HeroNote>
                        )}

                        <Examples>
                            Try{" "}
                            {EXAMPLES.map((pc, i) => (
                                <span key={pc}>
                                    {i > 0 && " · "}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setPostcode(pc);
                                            run(pc);
                                        }}
                                    >
                                        {pc}
                                    </button>
                                </span>
                            ))}
                        </Examples>
                    </HeroInner>
                </Container>
            </Hero>

            <Body empty={!report && !error}>
                <Container>
                    {/*
                     * Paper has no hero, so the report needs to say what it is
                     * and when it was run - a PDF outlives the prices in it.
                     */}
                    {report && (
                        <PrintHeader>
                            <h2>{chosen ? chosen.address : report.postcode}</h2>
                            <p>
                                {chosen ? report.postcode + " · " : ""}
                                Property report from {brandHost}, run on{" "}
                                {new Date().toLocaleDateString("en-GB", {
                                    day: "numeric",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </p>
                        </PrintHeader>
                    )}

                    {error && (
                        <Notice error className="no-print">
                            {error}
                        </Notice>
                    )}

                </Container>

                    {report && report.sample && report.sample.limited && (
                        <Container className="no-print">
                            {/*
                             * FIRST, ABOVE THE REPORT, because the question it
                             * answers - "is this all of it?" - is asked while
                             * scrolling, and an explanation underneath arrives
                             * after somebody has already decided the product is
                             * thin.
                             *
                             * no-print: a saved or shared PDF is a document
                             * about a property, and an upgrade prompt inside it
                             * is an advert in somebody's file.
                             */}
                            <SampleNotice>
                                <h2>This one is on us</h2>
                                <p>
                                    {report.sample.message ||
                                        "This is your one free property report."}
                                </p>
                                <p>
                                    A full report adds the instant valuation, comparable sales,
                                    running costs, schools, transport, crime, flood risk, solar
                                    potential and the environmental risk screening — and stays
                                    yours to re-open.
                                </p>
                                <SampleActions>
                                    <SampleBuy to="/account/buy">
                                        {report.sample.prices && report.sample.prices.single
                                            ? "Unlock the full report — £" +
                                              report.sample.prices.single.amount
                                            : "Unlock the full report"}
                                    </SampleBuy>
                                    <SampleAside to="/pricing">See all prices</SampleAside>
                                </SampleActions>
                            </SampleNotice>
                        </Container>
                    )}

                    {report && (
                        <>
                            <Container>
                            <Panel>
                                <PanelHead>
                                    <h2>{report.postcode}</h2>
                                    <Tag ok>Live</Tag>
                                </PanelHead>
                                <PanelBody>
                                    <Facts>
                                        <div>
                                            <dt>Council</dt>
                                            <dd>{report.location.council}</dd>
                                        </div>
                                        <div>
                                            <dt>Ward</dt>
                                            <dd>{report.location.ward}</dd>
                                        </div>
                                        <div>
                                            <dt>Constituency</dt>
                                            <dd>{report.location.constituency}</dd>
                                        </div>
                                        <div>
                                            <dt>Region</dt>
                                            <dd>{report.location.region}</dd>
                                        </div>
                                    </Facts>
                                </PanelBody>
                            </Panel>

                            {/*
                             * The same control again, so the choice can be
                             * changed after seeing the result without
                             * scrolling back to the hero.
                             *
                             * The two view switches sit in its foot. Everything
                             * in this card answers "what do I want to see and
                             * how", so splitting them across two boxes made a
                             * second box that was only ever two controls.
                             */}
                            <DataOptions
                                light
                                selected={selected}
                                onToggle={toggleOption}
                                onAll={() => setSelected(DATA_OPTIONS.map((o) => o.key))}
                                onNone={() => setSelected([])}
                            >
                            <SwitchRow>
                            <ViewSwitch>
                                <ViewLabel id="layout-label">Layout</ViewLabel>
                                <ViewToggle role="group" aria-labelledby="layout-label">
                                    <ViewButton
                                        type="button"
                                        active={!tiles}
                                        aria-pressed={!tiles}
                                        onClick={() => { setLayout("stacked"); writeStoredLayout("stacked"); }}
                                    >
                                        Stacked
                                    </ViewButton>
                                    <ViewButton
                                        type="button"
                                        active={tiles}
                                        aria-pressed={tiles}
                                        onClick={() => { setLayout("tiles"); writeStoredLayout("tiles"); }}
                                    >
                                        Tiles
                                    </ViewButton>
                                </ViewToggle>
                            </ViewSwitch>

                            {centre && (
                                <ViewSwitch>
                                    <ViewLabel id="view-label">Show as</ViewLabel>
                                    <ViewToggle role="group" aria-labelledby="view-label">
                                        <ViewButton
                                            type="button"
                                            active={view === "list"}
                                            aria-pressed={view === "list"}
                                            onClick={() => setView("list")}
                                        >
                                            List
                                        </ViewButton>
                                        <ViewButton
                                            type="button"
                                            active={view === "map"}
                                            aria-pressed={view === "map"}
                                            onClick={() => setView("map")}
                                        >
                                            Map
                                        </ViewButton>
                                    </ViewToggle>
                                </ViewSwitch>
                            )}
                            </SwitchRow>
                            </DataOptions>
                            </Container>

                            {view === "map" && centre && (
                                <>
                                    {/*
                                     * mapsReady gates the whole block, not just
                                     * the <GoogleMap>: the marker icons below
                                     * are built from window.google.maps and
                                     * throw if the script has not landed.
                                     */}
                                    <Container>
                                    {mapsReady ? (
                                        <MapFrame className="report-map">
                                            <GoogleMap
                                                mapContainerStyle={mapContainerStyle}
                                                center={centre}
                                                /*
                                                 * A fallback only. fitBounds
                                                 * below sets the real zoom
                                                 * from the circle, so the
                                                 * scale follows the frame
                                                 * instead of being guessed
                                                 * at one number for every
                                                 * screen size.
                                                 */
                                                zoom={14}
                                                onLoad={(map) => {
                                                    try {
                                                        // A throwaway Circle is the
                                                        // cheapest way to turn a
                                                        // centre and a radius into
                                                        // bounds; it is never added
                                                        // to the map.
                                                        const b = new window.google.maps.Circle({
                                                            center: centre,
                                                            // The widest ring in
                                                            // play, so nothing
                                                            // plotted falls off
                                                            // the edge.
                                                            radius: widestRingMetres,
                                                        }).getBounds();
                                                        // No padding: the circle
                                                        // should touch the edges,
                                                        // which is the whole point
                                                        // of the square frame.
                                                        if (b) map.fitBounds(b, 0);
                                                    } catch (err) {
                                                        /* Keep the fallback zoom. */
                                                    }
                                                }}
                                                options={{
                                                    styles: mapStyles,
                                                    disableDefaultUI: true,
                                                    zoomControl: true,
                                                    scrollwheel: false,
                                                    /*
                                                     * fitBounds can only pick a
                                                     * whole zoom level unless
                                                     * this is on, and it has to
                                                     * round DOWN to keep the
                                                     * bounds inside the frame.
                                                     * The exact fit for a 0.7
                                                     * mile ring on a tablet is
                                                     * z=14.92, so it was
                                                     * settling for 14 and
                                                     * showing 4271 m of map to
                                                     * hold a 2253 m circle -
                                                     * the ring filling 53% of
                                                     * the width and barely a
                                                     * third of the area, with
                                                     * the rest of the frame
                                                     * spent on ground the
                                                     * report says nothing
                                                     * about. With fractional
                                                     * zoom it lands on 14.92
                                                     * and the circle touches
                                                     * the edges.
                                                     */
                                                    isFractionalZoomEnabled: true,
                                                }}
                                                onClick={() => setOpenPin(null)}
                                            >
                                                {/*
                                                 * One ring per radius in play,
                                                 * so the reach of each thing is
                                                 * visible rather than being a
                                                 * number in a footnote. Drawn
                                                 * widest first: a later Circle
                                                 * sits over an earlier one, and
                                                 * the small rings need to be on
                                                 * top to be seen at all.
                                                 */}
                                                {rings.map((ring) => (
                                                    <Circle
                                                        key={ring.metres}
                                                        center={centre}
                                                        radius={ring.metres}
                                                        options={{
                                                            strokeColor: "#14315B",
                                                            strokeOpacity: 0.3,
                                                            strokeWeight: 1,
                                                            // Only the outermost is
                                                            // filled; stacking a
                                                            // wash per ring would
                                                            // darken the middle of
                                                            // the map.
                                                            fillColor: "#14315B",
                                                            fillOpacity: ring.outermost ? 0.04 : 0,
                                                            clickable: false,
                                                        }}
                                                    />
                                                ))}

                                                {pins.map((p, i) => (
                                                    <Marker
                                                        key={p.category + i}
                                                        position={{ lat: p.lat, lng: p.lon }}
                                                        title={p.name}
                                                        onClick={() => setOpenPin(p.category + i)}
                                                        icon={{
                                                            url: pinUrl(p.kind),
                                                            scaledSize: new window.google.maps.Size(26, 26),
                                                            // Centred on the place,
                                                            // not hanging below it.
                                                            anchor: new window.google.maps.Point(13, 13),
                                                        }}
                                                    >
                                                        {openPin === p.category + i && (
                                                            <InfoWindow onCloseClick={() => setOpenPin(null)}>
                                                                <InfoBody>
                                                                    <strong>{p.name}</strong>
                                                                    <span>
                                                                        {kindLabel(p.kind)} &middot;{" "}
                                                                        {distanceLabel(p.metres, p.miles)}
                                                                    </span>
                                                                </InfoBody>
                                                            </InfoWindow>
                                                        )}
                                                    </Marker>
                                                ))}

                                                {/* The postcode itself, last so it
                                                    draws over the rest. */}
                                                <Marker
                                                    position={centre}
                                                    title={report.postcode}
                                                    zIndex={999}
                                                    icon={{
                                                        url: "/images/pp-pin-letting.svg",
                                                        scaledSize: new window.google.maps.Size(40, 40),
                                                    }}
                                                />
                                            </GoogleMap>
                                        </MapFrame>
                                    ) : (
                                        <Notice>Loading the map&hellip;</Notice>
                                    )}

                                    {pinCategories.length > 0 && (
                                        <EstimateNote style={{ margin: "0 0 0.5rem" }}>
                                            The rings show how far each kind of thing is looked for:{" "}
                                            {[...pinCategories]
                                                .sort((a, b) => NEARBY_MILES[a] - NEARBY_MILES[b])
                                                .map((c) => PIN_LABELS[c].toLowerCase() + " " + NEARBY_MILES[c] + " mi")
                                                .join(", ")}
                                            . A shop worth walking to and a station worth walking to are not the
                                            same distance away, so a single ring round all of them would show you
                                            either too few stations or far too many shops.
                                        </EstimateNote>
                                    )}

                                    <MapLegend className="report-map-legend">
                                        {MAP_CATEGORIES.map((cat) => {
                                            const on = has(cat);
                                            /*
                                             * Stations are the one category
                                             * drawn with more than one mark,
                                             * so the row names each mode that
                                             * is actually plotted. The tick
                                             * box is still per CATEGORY -
                                             * "stations" is one data option,
                                             * and there is no way to ask the
                                             * API for tube but not tram.
                                             */
                                            const modes =
                                                cat === "stations"
                                                    ? pinKinds.filter((k) => k.indexOf("stations:") === 0)
                                                    : [];
                                            return (
                                                <LegendItem
                                                    key={cat}
                                                    on={on}
                                                    /* A printed key describes what is on
                                                       the paper map, so a row that is
                                                       switched off has nothing to explain
                                                       and does not travel. */
                                                    className={on ? undefined : "no-print"}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={on}
                                                        onChange={() => toggleOption(cat)}
                                                    />
                                                    {modes.length > 0 ? (
                                                        <>
                                                            {PIN_LABELS[cat]}
                                                            {modes.map((m) => (
                                                                <LegendMode key={m}>
                                                                    <Mark kind={m} />
                                                                    {kindLabel(m)}
                                                                </LegendMode>
                                                            ))}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Mark kind={cat} />
                                                            {PIN_LABELS[cat]}
                                                        </>
                                                    )}
                                                </LegendItem>
                                            );
                                        })}
                                    </MapLegend>
                                    </Container>
                                </>
                            )}

                            <Container>
                            <PanelGrid tiles={tiles}>
                            {has("councilTax") && report.councilTax && (
                            <Panel muted={!hasBands}>
                                <PanelHead muted={!hasBands}>
                                    <h2>Council tax</h2>
                                    <Tag ok={hasBands}>
                                        {hasBands
                                            ? tax.exact
                                                ? tax.year + " charges"
                                                : tax.year + " area average"
                                            : "Not held"}
                                    </Tag>
                                </PanelHead>
                                <PanelBody muted={!hasBands}>
                                    {hasBands ? (
                                        <>
                                            {/*
                                             * WE LOOKED AND COULD NOT TELL, which is not the same
                                             * as not having looked - and this used to say the
                                             * second thing. It read "we can't look that up
                                             * automatically", written when nothing here resolved a
                                             * band from an address. The register lookup landed
                                             * since, `band` arrives already set for most houses,
                                             * and the sentence survived to be read only in the
                                             * case where it is now wrong.
                                             *
                                             * What is actually true, measured 12 October 2026 on
                                             * the live API: 99 Streathbourne Road resolves to G and
                                             * the ground-floor flat at 89 to D, while the flats at
                                             * 73 come back absent. The register holds houses well
                                             * and flats badly, because a subdivided building is
                                             * often banded as the building.
                                             *
                                             * AND THERE ARE TWO FAILURES HERE, NOT ONE. The lookup
                                             * is keyed on the UPRN, and 17 of the 38 certificates
                                             * in SW18 1AA carry none - for those the register is
                                             * never asked at all. Saying "it doesn't list one
                                             * against this address" there would be the same shape
                                             * of lie as the sentence this replaced: a claim to have
                                             * checked something we did not. So the two cases say
                                             * different things, and the reader is told which one
                                             * they are in.
                                             *
                                             * The link stays in both. The VOA's own checker has
                                             * every property, and we point at it rather than
                                             * compiling it: they refused a freedom of information
                                             * request for this data in bulk under an absolute
                                             * exemption, holding that the per-property search
                                             * already discharges their duty to publish.
                                             */}
                                            {chosen && !band && (
                                                <Reason style={{ marginBottom: "0.85rem" }}>
                                                    No band is available for {chosen.address}.{" "}
                                                    {chosen.uprn ? (
                                                        <>
                                                            The council tax register we search doesn&rsquo;t list
                                                            one against this address &mdash; common for flats and
                                                            subdivided houses, where the band is often recorded
                                                            against the building rather than each home.
                                                        </>
                                                    ) : (
                                                        <>
                                                            This address has no property reference on its energy
                                                            certificate, which is what the band lookup is keyed
                                                            on, so we couldn&rsquo;t search for it.
                                                        </>
                                                    )}{" "}
                                                    <a
                                                        href="https://www.tax.service.gov.uk/check-council-tax-band/search"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Look the band up on the VOA register
                                                    </a>
                                                    , then pick it below to see the charge.
                                                </Reason>
                                            )}

                                            <Bands>
                                                <thead>
                                                    <tr>
                                                        <th>Band</th>
                                                        <th>Per year</th>
                                                        <th>Per month</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {tax.bands
                                                        .filter(
                                                            (b) =>
                                                                !chosen || !band || showAllBands || b.band === band
                                                        )
                                                        .map((b) => (
                                                        <tr key={b.band} data-selected={band === b.band}>
                                                            <td>
                                                                <BandButton
                                                                    type="button"
                                                                    selected={band === b.band}
                                                                    aria-pressed={band === b.band}
                                                                    aria-label={"Select band " + b.band}
                                                                    onClick={() =>
                                                                        setBand(band === b.band ? null : b.band)
                                                                    }
                                                                >
                                                                    {b.band}
                                                                </BandButton>
                                                            </td>
                                                            <td>{money(b.annual)}</td>
                                                            <td>{money(b.monthly)}</td>
                                                        </tr>
                                                        ))}
                                                </tbody>
                                            </Bands>

                                            {chosen && band && (
                                                <BandsFoot>
                                                    Band {band} for {chosen.address}
                                                    {tax.propertyBand && tax.propertyBand.band === band
                                                        ? tax.propertyBand.source === "listing"
                                                            ? ", from our own records for this property"
                                                            : ", from the council tax register"
                                                        : ", as you selected"}
                                                    .{" "}
                                                    {/*
                                                     * Screen only, punctuation included. "Pick the
                                                     * right band above" is an instruction to press
                                                     * something, which paper cannot offer - and
                                                     * hiding the control without the full stop that
                                                     * closes its sentence strands the stop.
                                                     */}
                                                    <span className="no-print">
                                                        If that is wrong, pick the right band above.{" "}
                                                        <LinkButton
                                                            type="button"
                                                            onClick={() => setShowAllBands(!showAllBands)}
                                                        >
                                                            {showAllBands
                                                                ? "Show only this band"
                                                                : "Show all eight bands"}
                                                        </LinkButton>
                                                    </span>
                                                </BandsFoot>
                                            )}

                                            <BandsFoot>
                                                {tax.exact ? (
                                                    <>
                                                        Charges set by {tax.council} for {tax.year}, including the
                                                        precept for the Greater London Authority.{" "}
                                                        <a href={tax.source} target="_blank" rel="noopener noreferrer">
                                                            See the council&rsquo;s own schedule
                                                        </a>
                                                        .
                                                    </>
                                                ) : (
                                                    <>
                                                        Based on the average Band D for the {tax.council} area in{" "}
                                                        {tax.year}, published by {tax.publisher || "MHCLG"}. Where a parish or community
                                                        council sets its own precept, the charge for an individual home
                                                        differs from this average.{" "}
                                                        <a href={tax.source} target="_blank" rel="noopener noreferrer">
                                                            See the published figures
                                                        </a>
                                                        .
                                                    </>
                                                )}{" "}
                                                {/* "Above" means the band buttons, which paper has not got. */}
                                                <span className="no-print">Select your band above, or{" "}</span>
                                                <span className="print-only">You can{" "}</span>
                                                <a
                                                    href="https://www.tax.service.gov.uk/check-council-tax-band/search"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    look it up on the VOA register
                                                </a>
                                                . Figures are indicative and not a substitute for your bill.
                                            </BandsFoot>
                                        </>
                                    ) : (
                                        <Reason>{tax.reason}</Reason>
                                    )}
                                </PanelBody>
                            </Panel>
                            )}
                            {has("runningCosts") && report && (
                            (() => {
                                /*
                                 * Four bills that arrive whoever lives here,
                                 * added up. Nothing is modelled: each line is
                                 * somebody's published figure.
                                 *
                                 * TWO KINDS OF NUMBER SIT IN THIS PANEL and the
                                 * difference matters more than the total does.
                                 *
                                 * Council tax and energy are about THIS
                                 * PROPERTY - the charge for the band in play,
                                 * and the assessor's own estimate for the
                                 * building, restated to today's cap. Water and
                                 * broadband are about everyone ELSE: the water
                                 * company's average household bill, and what
                                 * the country spends on a broadband line. They
                                 * cannot be about this property, because a
                                 * water bill turns on whether there is a meter
                                 * and how many people are behind it, and a
                                 * broadband price turns on which deal somebody
                                 * signed.
                                 *
                                 * So the rows are ordered property-first, the
                                 * footnote says of each line what it is, and
                                 * the total is called a total of these four
                                 * rather than what this home costs. Adding two
                                 * averages to two measurements and presenting
                                 * the sum as a measurement would undo the care
                                 * in the first two.
                                 */
                                const tax = report.councilTax;
                                const rc = report.runningCosts || {};
                                const waterInfo = rc.water || null;
                                const bbInfo = rc.broadband || null;
                                const cert =
                                    report.certificate && report.certificate.status === "found"
                                        ? report.certificate
                                        : null;

                                /*
                                 * "derived" is what the council tax lookup
                                 * reports, not "found" - the eight bands are
                                 * derived from band D rather than published as
                                 * a set. Tested on the rows themselves so a
                                 * third status cannot silently blank this.
                                 */
                                const bandRow =
                                    tax && Array.isArray(tax.bands) && band
                                        ? tax.bands.find((b) => b.band === band)
                                        : null;
                                const taxMonthly = bandRow ? bandRow.monthly : null;

                                // Today's prices where the fuel is capped,
                                // otherwise the certificate's own year, which
                                // the footnote names.
                                const today =
                                    cert && cert.todaysCost && cert.todaysCost.status === "estimated"
                                        ? cert.todaysCost
                                        : null;
                                const energyAnnual = today
                                    ? today.total
                                    : cert && cert.annualCost
                                    ? cert.annualCost.total
                                    : null;
                                const energyMonthly = energyAnnual != null ? energyAnnual / 12 : null;

                                const waterMonthly =
                                    waterInfo && waterInfo.status === "found" ? waterInfo.monthly : null;
                                const bbMonthly = bbInfo && bbInfo.status === "found" ? bbInfo.monthly : null;

                                /*
                                 * The total is of the rows that have a figure,
                                 * and the tag says how many that is. A sum of
                                 * three lines presented as a sum of four is the
                                 * one way this panel could lie by arithmetic
                                 * rather than by wording.
                                 */
                                const rows = [taxMonthly, energyMonthly, waterMonthly, bbMonthly];
                                const known = rows.filter((v) => v != null);
                                const all = known.length === rows.length;
                                const total = known.length ? known.reduce((a, b) => a + b, 0) : null;
                                const live = known.length > 0;

                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Monthly running costs</h2>
                                            <Tag ok={all}>
                                                {all
                                                    ? money(total) + " a month"
                                                    : live
                                                    ? money(total) + " a month, " + known.length + " of 4"
                                                    : "Pick a property"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    <NearList>
                                                        <NearRow>
                                                            <NearName>
                                                                Council tax{band ? " (band " + band + ")" : ""}
                                                            </NearName>
                                                            <NearDistance>
                                                                {taxMonthly != null ? money(taxMonthly) : DASH}
                                                            </NearDistance>
                                                        </NearRow>
                                                        <NearRow>
                                                            <NearName>Gas and electricity</NearName>
                                                            <NearDistance>
                                                                {energyMonthly != null ? money(energyMonthly) : DASH}
                                                            </NearDistance>
                                                        </NearRow>
                                                        <NearRow>
                                                            <NearName>Water</NearName>
                                                            <NearDistance>
                                                                {waterMonthly != null ? money(waterMonthly) : DASH}
                                                            </NearDistance>
                                                            {waterInfo && waterInfo.company && (
                                                                <NearDetail>
                                                                    {waterInfo.company}
                                                                    {waterInfo.status === "found" &&
                                                                        !waterInfo.includesSewerage &&
                                                                        " · water only, sewerage billed separately"}
                                                                </NearDetail>
                                                            )}
                                                        </NearRow>
                                                        <NearRow>
                                                            <NearName>Broadband</NearName>
                                                            <NearDistance>
                                                                {bbMonthly != null ? money(bbMonthly) : DASH}
                                                            </NearDistance>
                                                            <NearDetail>UK average for one line</NearDetail>
                                                        </NearRow>
                                                        {live && (
                                                            <NearRow>
                                                                <NearName>
                                                                    <strong>
                                                                        {all
                                                                            ? "Total"
                                                                            : "Total of " + known.length + " of 4"}
                                                                    </strong>
                                                                </NearName>
                                                                <NearDistance>
                                                                    <strong>{money(total)}</strong>
                                                                </NearDistance>
                                                            </NearRow>
                                                        )}
                                                    </NearList>
                                                    <BandsFoot>
                                                        {taxMonthly != null && (
                                                            <>
                                                                Council tax is the year&rsquo;s charge spread over
                                                                twelve. Most councils bill over ten instalments
                                                                from April, which makes each one about a fifth
                                                                larger than the figure here.{" "}
                                                            </>
                                                        )}
                                                        {energyMonthly != null && (
                                                            <>
                                                                Energy is the assessor&rsquo;s estimate for a
                                                                typical household in this property
                                                                {today
                                                                    ? ", restated to the " + today.capPeriod + " price cap"
                                                                    : cert && cert.annualCost
                                                                    ? ", at the prices of " + cert.annualCost.pricedAt
                                                                    : ""}
                                                                . It is not a quote and not your bill.{" "}
                                                            </>
                                                        )}
                                                        {waterInfo && waterInfo.status === "found" && (
                                                            <>
                                                                <strong>
                                                                    Water is an average, not this
                                                                    property&rsquo;s bill.
                                                                </strong>{" "}
                                                                It is {waterInfo.company}&rsquo;s average household
                                                                charge for {waterInfo.year}, from{" "}
                                                                {waterInfo.source}. A metered home pays for what it
                                                                uses and an unmetered one pays on a rateable value
                                                                set in 1973, so the household behind the meter
                                                                moves this figure far more than the house does.
                                                                {!waterInfo.includesSewerage &&
                                                                    " " +
                                                                        waterInfo.company +
                                                                        " supplies water only here; sewerage is billed separately by the regional company and is not in this figure."}{" "}
                                                            </>
                                                        )}
                                                        {waterInfo &&
                                                            (waterInfo.status === "unavailable" ||
                                                                waterInfo.status === "none" ||
                                                                waterInfo.status === "notCharged") && (
                                                                <>{waterInfo.reason} </>
                                                            )}
                                                        {bbMonthly != null && bbInfo && (
                                                            <>
                                                                <strong>Broadband is what the country spends</strong>
                                                                , not a price quoted here: {bbInfo.source}
                                                                &rsquo;s average monthly spend on a standalone line
                                                                in {bbInfo.period}. What can actually be bought at
                                                                this address is the broadband panel, not this one.{" "}
                                                            </>
                                                        )}
                                                        Insurance, TV licence, and any service charge or ground
                                                        rent are not included.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>
                                                    Nothing to add up for this postcode yet. Choose an address and
                                                    run the property report for the council tax and energy, which
                                                    are the two halves that depend on the property itself.
                                                </Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}


                            {has("epc") && report.epc && (
                            (() => {
                                const epc = report.epc;
                                const cert = report.certificate;
                                const list = epc.status === "found" ? epc.certificates : null;
                                const live = cert && cert.status === "found";
                                const hasList = !!(list && list.length);

                                return (
                                    <Panel muted={!hasList}>
                                        <PanelHead muted={!hasList}>
                                            <h2>Energy performance</h2>
                                            <Tag ok={live}>
                                                {live
                                                    ? "EPC " + bandFor(cert.currentRating)
                                                    : hasList
                                                    ? list.length + " certificates"
                                                    : "None on record"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!hasList}>
                                            {!hasList && (
                                                <Reason>
                                                    {epc.status === "none"
                                                        ? "No EPC has been lodged for this postcode. Around a fifth of homes have no certificate — typically those not sold or let since 2008."
                                                        : epc.reason || "No certificate found."}
                                                </Reason>
                                            )}

                                            {hasList && !live && (
                                                <Reason>
                                                    {certLoading ? (
                                                        "Loading certificate…"
                                                    ) : (
                                                        <>
                                                            {/*
                                                             * The address list is a control, so it
                                                             * does not print - and on paper this
                                                             * was pointing at something that is
                                                             * not there. Same instruction, aimed
                                                             * at where the reader actually is.
                                                             */}
                                                            <span className="no-print">
                                                                Choose your property from the address list above,
                                                                then &ldquo;Property Report&rdquo;, to see
                                                            </span>
                                                            <span className="print-only">
                                                                Run this report again for a single address to see
                                                            </span>{" "}
                                                            its energy rating, floor area and running costs.
                                                        </>
                                                    )}
                                                </Reason>
                                            )}

                                            {live && (
                                                <>
                                                    {chosen && (
                                                        <Reason style={{ marginBottom: "0.85rem" }}>
                                                            {chosen.address}
                                                        </Reason>
                                                    )}

                                                    <Scale>
                                                        {EPC_BANDS.map((b, i) => (
                                                            <ScaleRow
                                                                key={b[0]}
                                                                band={b[0]}
                                                                i={i}
                                                                active={b[0] === bandFor(cert.currentRating)}
                                                            >
                                                                <span>{b[0]}</span>
                                                                <ScaleMark>
                                                                    {b[0] === bandFor(cert.currentRating)
                                                                        ? "this home — " + cert.currentRating
                                                                        : b[1] + "–" + b[2]}
                                                                </ScaleMark>
                                                            </ScaleRow>
                                                        ))}
                                                    </Scale>

                                                    {(() => {
                                                        const today = cert.todaysCost || {};
                                                        const estimated = today.status === "estimated";
                                                        const year = String(cert.annualCost.pricedAt || "").slice(0, 4);

                                                        return (
                                                            <>
                                                                <CostGrid>
                                                                    <div>
                                                                        <dt>Heating</dt>
                                                                        <dd>
                                                                            {pounds(
                                                                                estimated && known(today.heating)
                                                                                    ? today.heating
                                                                                    : cert.annualCost.heating
                                                                            )}
                                                                        </dd>
                                                                    </div>
                                                                    <div>
                                                                        <dt>Hot water</dt>
                                                                        <dd>
                                                                            {pounds(
                                                                                estimated && known(today.hotWater)
                                                                                    ? today.hotWater
                                                                                    : cert.annualCost.hotWater
                                                                            )}
                                                                        </dd>
                                                                    </div>
                                                                    <div>
                                                                        <dt>Lighting</dt>
                                                                        <dd>
                                                                            {pounds(
                                                                                estimated && known(today.lighting)
                                                                                    ? today.lighting
                                                                                    : cert.annualCost.lighting
                                                                            )}
                                                                        </dd>
                                                                    </div>
                                                                    <div>
                                                                        <dt>Total a year</dt>
                                                                        <dd>
                                                                            {pounds(
                                                                                estimated && known(today.total)
                                                                                    ? today.total
                                                                                    : cert.annualCost.total
                                                                            )}
                                                                        </dd>
                                                                    </div>
                                                                </CostGrid>

                                                                {/*
                                                                 * When the figures shown are restated, the
                                                                 * certificate's own total stays visible. It is
                                                                 * the audited number, and a reader comparing
                                                                 * against the PDF certificate should find it.
                                                                 */}
                                                                {estimated && known(today.total) && (
                                                                    <EstimateNote>
                                                                        Estimated at today&rsquo;s energy prices.
                                                                        The certificate itself says{" "}
                                                                        {pounds(cert.annualCost.total)} a year at{" "}
                                                                        {year} prices.
                                                                    </EstimateNote>
                                                                )}
                                                            </>
                                                        );
                                                    })()}

                                                    {(() => {
                                                        /*
                                                         * MEES, the Minimum Energy Efficiency Standard. A landlord
                                                         * may not let a home below band E, and the proposal is to
                                                         * raise that to C.
                                                         *
                                                         * THE DATES CHANGED AND THIS WAS WRONG FOR A WHILE. It
                                                         * said "C for new tenancies from 2028 and all tenancies
                                                         * from 2030", which was the shape of the 2021 consultation
                                                         * and the 2025 proposal. The Warm Homes Plan of 21 January
                                                         * 2026 dropped the phasing: one date, 1 October 2030, for
                                                         * every tenancy, with the cost cap raised from £3,500 to
                                                         * £10,000 and the maximum penalty from £5,000 to £30,000.
                                                         * Work done from October 2025 counts towards the cap.
                                                         *
                                                         * Still "proposed", and that word is doing real work: the
                                                         * plan is a statement of intent, not law. It needs primary
                                                         * powers and then a statutory instrument amending the
                                                         * Energy Efficiency (Private Rented Property) (England and
                                                         * Wales) Regulations 2015. Telling a landlord their house
                                                         * fails a standard that does not yet exist would be worse
                                                         * than saying nothing - and so would telling them the
                                                         * deadline is two years earlier than it is.
                                                         *
                                                         * A dated page beats an undated one here: anyone reading
                                                         * this after the statutory instrument lands needs to know
                                                         * which announcement it was written against.
                                                         *
                                                         * No new lookup: the band is already on the certificate
                                                         * above. It is read differently for a landlord than for a
                                                         * buyer, and the certificate does not say so anywhere.
                                                         */
                                                        const letter = bandFor(cert.currentRating);
                                                        if (!letter || !/^[A-G]$/.test(letter)) return null;
                                                        const rank = letter.charCodeAt(0) - 64; // A=1 … G=7
                                                        const belowE = rank > 5;
                                                        const belowC = rank > 3;
                                                        if (!belowC) return null;
                                                        return (
                                                            <Notice error={belowE} style={{ margin: "0 0 1rem" }}>
                                                                <strong>Letting this home:</strong>{" "}
                                                                {belowE
                                                                    ? "band " +
                                                                      letter +
                                                                      " is below the current minimum of E, so it cannot normally be let on a new or existing tenancy without an exemption registered on the PRS Exemptions Register."
                                                                    : "band " +
                                                                      letter +
                                                                      " meets today's minimum of E, but falls short of C, the standard proposed for rented homes by 1 October 2030 — one date for every tenancy, not phased. The Warm Homes Plan of January 2026 caps what a landlord must spend at £10,000, counting work done from October 2025. It is an announced intention and not yet law."}
                                                            </Notice>
                                                        );
                                                    })()}

                                                    <Facts>
                                                        <div>
                                                            <dt>Floor area</dt>
                                                            <dd>{withUnit(cert.floorAreaSqm, " m²")}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Habitable rooms</dt>
                                                            <dd>{withUnit(cert.habitableRooms, "")}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Potential rating</dt>
                                                            <dd>
                                                                {known(cert.potentialRating)
                                                                    ? bandFor(cert.potentialRating) + " (" + cert.potentialRating + ")"
                                                                    : DASH}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt>CO&#8322; a year</dt>
                                                            <dd>{withUnit(cert.co2Current, " t")}</dd>
                                                        </div>
                                                    </Facts>

                                                    <BandsFoot>
                                                        Assessed {cert.inspectionDate}. Running costs are the
                                                        assessor&rsquo;s estimate for a typical household, not a quote
                                                        and not your bill.
                                                        {cert.todaysCost && cert.todaysCost.status === "estimated" && (
                                                            <>
                                                                {" "}
                                                                The certificate&rsquo;s costs were worked out at the
                                                                energy prices of the year it was assessed, so they have
                                                                been scaled by the change in average household gas and
                                                                electricity prices since then, to the{" "}
                                                                <a
                                                                    href="https://www.ofgem.gov.uk/information-consumers/energy-advice-households/energy-price-cap-unit-rates-and-standing-charges"
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    Ofgem price cap
                                                                </a>{" "}
                                                                for {cert.todaysCost.capPeriod}. That is an
                                                                approximation, not a recalculation of the certificate.
                                                            </>
                                                        )}
                                                    </BandsFoot>
                                                </>
                                            )}

                                            {/* The address data notice sits under the dropdown in the hero. */}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {has("soldPrices") && report.soldPrices && (
                            (() => {
                                const sp = report.soldPrices;
                                const live = sp.status === "found";
                                const all = live ? sp.sales : [];
                                const matched = all.filter((s) => s.likelyMatch);

                                /*
                                 * With a property chosen, show its own sales. Land Registry
                                 * record no property reference, so the match is on address
                                 * text and is never asserted as certain - hence the toggle
                                 * back to the whole postcode, which is also what someone
                                 * sees when the match finds nothing.
                                 */
                                const narrowed = !!chosen && !showAllSales && matched.length > 0;
                                const sales = narrowed ? matched : all;

                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Sold prices</h2>
                                            <Tag ok={live}>
                                                {!live
                                                    ? "Not loaded"
                                                    : narrowed
                                                    ? matched.length === 1
                                                        ? "1 for this property"
                                                        : matched.length + " for this property"
                                                    : all.length + " in this postcode"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    {chosen && matched.length === 0 && (
                                                        <Reason style={{ marginBottom: "0.85rem" }}>
                                                            No recorded sale matches {chosen.address}. It may not have
                                                            changed hands in the period we hold, or Land Registry may
                                                            record its address differently. Showing the whole postcode.
                                                        </Reason>
                                                    )}

                                                    <Sales>
                                                        {sales.slice(0, 12).map((s, i) => (
                                                            <SaleRow key={i} match={s.likelyMatch}>
                                                                <SaleWhen>{formatDate(s.date)}</SaleWhen>
                                                                <SaleWhere>
                                                                    {s.address}
                                                                    {s.likelyMatch && <Match>this address</Match>}
                                                                </SaleWhere>
                                                                <SaleWhat>{PROPERTY_TYPES[s.propertyType] || ""}</SaleWhat>
                                                                <SalePrice>{pounds(s.price)}</SalePrice>
                                                            </SaleRow>
                                                        ))}
                                                    </Sales>
                                                    <BandsFoot>
                                                        {sales.length > 12 &&
                                                            "Showing the 12 most recent of " + sales.length + ". "}
                                                        {narrowed ? (
                                                            <>
                                                                Sales recorded at {chosen.address}. Land Registry hold
                                                                no property reference, only an address, so these are
                                                                matched on the address text rather than with certainty.{" "}
                                                                <span className="no-print">
                                                                    <LinkButton
                                                                        type="button"
                                                                        onClick={() => setShowAllSales(true)}
                                                                    >
                                                                        Show all {all.length} in the postcode
                                                                    </LinkButton>
                                                                    .{" "}
                                                                </span>
                                                            </>
                                                        ) : chosen && matched.length > 0 ? (
                                                            <>
                                                                All sales in the postcode.{" "}
                                                                <span className="no-print">
                                                                    <LinkButton
                                                                        type="button"
                                                                        onClick={() => setShowAllSales(false)}
                                                                    >
                                                                        Show only {chosen.address}
                                                                    </LinkButton>
                                                                    .{" "}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            "Land Registry record no property reference, only an address, so sales cannot be tied to one home with certainty. These are all sales in the postcode. "
                                                        )}
                                                        Contains HM Land Registry data &copy; Crown copyright and
                                                        database right 2026.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{sp.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {has("broadband") && report.broadband && (
                            (() => {
                                const bb = report.broadband;
                                const live = bb.status === "found";

                                /*
                                 * MOBILE IS A DIFFERENT RESOLUTION FROM EVERYTHING
                                 * ELSE IN THIS PANEL, and that is the only thing
                                 * the card has to get right about it. Ofcom publish
                                 * fixed broadband postcode by postcode and mobile
                                 * only by local authority - there is no open
                                 * postcode-level mobile coverage anywhere.
                                 *
                                 * So the borough is named on every line of it. In a
                                 * London borough these figures are saturated and
                                 * will read 100 whatever the address, which is true
                                 * and unremarkable; in Powys a quarter of premises
                                 * cannot get indoor 4G from all four networks. The
                                 * row earns its place out there and on the agent
                                 * tier, where supply of a mobile signal is one of
                                 * MHCLG's material information categories and has
                                 * to be carried whatever it says.
                                 */
                                const mob = bb.mobile && bb.mobile.status === "found" ? bb.mobile : null;

                                // Ofcom report availability as a percentage of
                                // premises in the postcode. On a terrace where
                                // one house is not yet passed, 100% and 97% mean
                                // materially different things to that household,
                                // so show the figure rather than a yes/no.
                                const headline = live
                                    ? bb.gigabit >= 100
                                        ? "Gigabit available"
                                        : bb.gigabit > 0
                                        ? "Gigabit partly available"
                                        : bb.superfast >= 100
                                        ? "Superfast available"
                                        : "Limited"
                                    : null;

                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Broadband{mob ? " and mobile" : ""}</h2>
                                            <Tag ok={live}>{live ? headline : "Not loaded"}</Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    <CostGrid>
                                                        <div>
                                                            <dt>Gigabit</dt>
                                                            <dd>{bb.gigabit}%</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Ultrafast</dt>
                                                            <dd>{bb.ultrafast}%</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Superfast</dt>
                                                            <dd>{bb.superfast}%</dd>
                                                        </div>
                                                        <div>
                                                            <dt>300 Mbit/s or more</dt>
                                                            <dd>{bb.over300}%</dd>
                                                        </div>
                                                    </CostGrid>
                                                    {mob && (
                                                        <>
                                                            <SubHead>
                                                                Mobile in {mob.authority}
                                                            </SubHead>
                                                            <CostGrid>
                                                                <div>
                                                                    <dt>Indoor 4G, all four networks</dt>
                                                                    <dd>{mob.indoor4gAll}%</dd>
                                                                </div>
                                                                <div>
                                                                    <dt>Outdoor 5G, all four networks</dt>
                                                                    <dd>{mob.outdoor5gAll}%</dd>
                                                                </div>
                                                                {mob.indoor4gNone > 0 && (
                                                                    <div>
                                                                        <dt>No indoor 4G at all</dt>
                                                                        <dd>{mob.indoor4gNone}%</dd>
                                                                    </div>
                                                                )}
                                                            </CostGrid>
                                                        </>
                                                    )}
                                                    <BandsFoot>
                                                        Percentage of homes in this postcode that can get each service.
                                                        {bb.noDecent > 0 &&
                                                            " " +
                                                                bb.noDecent +
                                                                "% cannot get decent broadband from a fixed line."}{" "}
                                                        Source: {bb.release}. Availability is not the same as what a
                                                        given property is currently connected to.
                                                        {mob && (
                                                            <>
                                                                {" "}
                                                                <strong>
                                                                    The mobile figures are for the whole of{" "}
                                                                    {mob.authority}
                                                                </strong>
                                                                , not this postcode
                                                                {mob.premises
                                                                    ? " — " +
                                                                      mob.premises.toLocaleString("en-GB") +
                                                                      " premises averaged together"
                                                                    : ""}
                                                                . Ofcom publish fixed broadband postcode by postcode
                                                                and mobile only by local authority, so this is the
                                                                finest cut there is. Indoor signal in particular turns
                                                                on the building &mdash; its walls, its glazing, which
                                                                floor you are on &mdash; so treat it as what the area
                                                                is like rather than what this home will get.
                                                            </>
                                                        )}
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{bb.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {/*
                              * SOLAR AND RISKS SIT HERE, ABOVE SCHOOLS, AND NOT
                              * FURTHER DOWN WITH PLANNING WHERE THEY READ MORE
                              * NATURALLY. Solar belongs beside energy performance
                              * anyway - but the binding reason is that the pinned
                              * eslint-plugin-react-hooks reports every hook in this
                              * component as conditionally called when ANYTHING is
                              * added to the JSX below the crime panel. A bare
                              * <Panel /> does it; so does a one-line call site.
                              * Inserted here instead, the same markup is clean.
                              *
                              * It is a false positive - the hooks are all top
                              * level and none of them move - and the real fix is
                              * to break this 3,000-line render into components,
                              * which is a job of its own. Until then: add new
                              * panels ABOVE this point, and check with
                              * `npx eslint --resolve-plugins-relative-to . <file>`
                              * from front/, which reproduces the build. A bare
                              * `npx eslint` with an ad-hoc config does not.
                              */}
                            {has("solar") && report.solar && (
                            (() => {
                                const s = report.solar;
                                const sys = s.system || {};
                                const roof = s.roof || {};
                                const batt = s.battery || {};
                                const a = s.assumptions || {};
                                const money = (n) => "\u00A3" + Number(n).toLocaleString();

                                /*
                                 * EVERY CONDITIONAL IS RESOLVED HERE, NOT IN THE
                                 * JSX BELOW, and that is not a style preference.
                                 * Inline ternaries inside this `&&` gate make the
                                 * pinned eslint-plugin-react-hooks report every
                                 * hook in the file as conditionally called - 36 of
                                 * them, none of which moved. Same trap as the notes
                                 * on sharePostcode and canValue further up. The
                                 * build treats it as an error, so it does not ship.
                                 */
                                const rows = [];
                                rows.push([
                                    "Panels that fit",
                                    roof.usableAreaM2
                                        ? sys.maxPanels + " on " + roof.usableAreaM2 + " m\u00B2 of usable roof"
                                        : String(sys.maxPanels),
                                ]);
                                if (sys.annualGenerationKwh != null) {
                                    rows.push([
                                        "Generation a year",
                                        sys.annualGenerationKwh.toLocaleString() + " kWh",
                                    ]);
                                }
                                if (sys.annualSavings != null) {
                                    const extra = sys.annualExportIncome
                                        ? ", plus " + money(sys.annualExportIncome) + " exported"
                                        : "";
                                    rows.push(["Saved on bills", money(sys.annualSavings) + " a year" + extra]);
                                }
                                if (sys.costEstimate != null) {
                                    const back = sys.paybackYears
                                        ? ", paying back in about " + Math.round(sys.paybackYears) + " years"
                                        : "";
                                    rows.push(["System cost", money(sys.costEstimate) + back]);
                                }
                                if (batt.annualSavings != null) {
                                    const all = batt.totalCost ? " for " + money(batt.totalCost) + " all in" : "";
                                    rows.push(["With a battery", money(batt.annualSavings) + " a year" + all]);
                                }
                                if (sys.co2SavedKg != null) {
                                    rows.push(["Carbon saved", sys.co2SavedKg.toLocaleString() + " kg a year"]);
                                }

                                const tag = sys.sizeKwp ? sys.sizeKwp + " kWp" : sys.maxPanels + " panels";

                                /*
                                 * THE ASSUMPTIONS ARE THE POINT, not small print.
                                 * The same roof is worth a very different figure at
                                 * 30p a unit than at 24.5p, and anybody holding an
                                 * installer's quote needs to know which was used
                                 * before comparing the two.
                                 */
                                const notes = [
                                    "An estimate from the building\u2019s footprint and roof shape, not a survey \u2014 nobody has been on this roof.",
                                ];
                                if (a.electricityRatePPerKwh) {
                                    const exp = a.exportRatePPerKwh
                                        ? " and " + a.exportRatePPerKwh + "p for anything exported"
                                        : "";
                                    notes.push("Assumes " + a.electricityRatePPerKwh + "p a unit for electricity" + exp + ".");
                                }
                                if (roof.orientation) notes.push("Roof orientation read as " + roof.orientation + ".");
                                if (s.caveat) notes.push(s.caveat);
                                if (roof.hasExistingSolar) notes.push("This roof already appears to have panels on it.");
                                if (s.confidence) notes.push("Confidence: " + s.confidence + ".");
                                if (s.source) notes.push("Source: " + s.source + ".");

                                return (
                                    <Panel>
                                        <PanelHead>
                                            <h2>Solar potential</h2>
                                            <Tag ok>{tag}</Tag>
                                        </PanelHead>
                                        <PanelBody>
                                            <Bands>
                                                <tbody>
                                                    {rows.map((row) => (
                                                        <tr key={row[0]}>
                                                            <th>{row[0]}</th>
                                                            <td>{row[1]}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </Bands>
                                            <BandsFoot>{notes.join(" ")}</BandsFoot>
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}
                            {has("environmentalRisks") && report.environmentalRisks && (
                            (() => {
                                const risks = (report.environmentalRisks || {}).risks || [];
                                const flagged = risks.filter((r) => r.anyDetected);

                                /* Hoisted for the same reason as the solar panel above. */
                                const nothingFound = flagged.length === 0;
                                let riskTag = flagged.length + " to look at";
                                if (nothingFound) riskTag = "Nothing flagged";
                                else if (flagged.length === 1) riskTag = "1 to look at";

                                /*
                                 * The tag counts what was FOUND, not what was
                                 * checked. "5 checked" on a property with knotweed
                                 * next door reads as reassurance.
                                 */
                                const groups = risks.map((r) => ({
                                    key: r.type,
                                    heading: r.heading,
                                    source: r.source || "",
                                    rows: (r.entries || []).map((e, i) => ({
                                        key: r.type + "-" + i,
                                        name: e.detail ? e.name + " \u2014 " + e.detail : e.name,
                                        label: e.label || "",
                                    })),
                                }));

                                return (
                                    <Panel>
                                        <PanelHead>
                                            <h2>Radon, mining and other risks</h2>
                                            <Tag ok={nothingFound}>{riskTag}</Tag>
                                        </PanelHead>
                                        <PanelBody>
                                            {groups.map((g) => (
                                                <div key={g.key} style={{ marginBottom: "1.1rem" }}>
                                                    <strong>{g.heading}</strong>
                                                    <NearList>
                                                        {g.rows.map((row) => (
                                                            <NearRow key={row.key}>
                                                                <NearName>{row.name}</NearName>
                                                                <NearDistance>{row.label}</NearDistance>
                                                            </NearRow>
                                                        ))}
                                                    </NearList>
                                                    <Reason>{g.source}</Reason>
                                                </div>
                                            ))}
                                            <BandsFoot>
                                                Screening from published datasets at this address, not a
                                                survey. A clear result is not a guarantee and a flagged one is
                                                not a defect &mdash; both are reasons to ask a surveyor or
                                                conveyancer, who can look at the property itself. Flood risk
                                                is shown separately in the planning panel, from the
                                                Environment Agency.
                                            </BandsFoot>
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}
                            {showPanel("schools") && report.schools && (
                            (() => {
                                const sc = report.schools || {};
                                const live = sc.status === "found";
                                const total = live ? sc.primary.length + sc.secondary.length : 0;
                                const CAP = 6;

                                /*
                                 * OFSTED, AND THE TWO FRAMEWORKS IT ARRIVES IN.
                                 *
                                 * Up to September 2024 a school got one word -
                                 * Outstanding, Good, Requires improvement,
                                 * Inadequate. From November 2025 it gets a report
                                 * card: separate grades for curriculum,
                                 * achievement, behaviour and so on, with no
                                 * overall word, because Ofsted removed it on
                                 * purpose after Ruth Perry's death.
                                 *
                                 * So this does not invent one. A report card is
                                 * summarised as the count at each standard -
                                 * "2 strong, 4 expected" - which says the same
                                 * thing the grid says without pretending to be a
                                 * verdict. An old grade is shown as the word it
                                 * is, with its year, so nobody reads a 2019
                                 * judgement as current.
                                 */
                                const STANDARDS = [
                                    ["Exceptional", "exceptional"],
                                    ["Strong standard", "strong"],
                                    ["Expected standard", "expected"],
                                    ["Needs attention", "needs attention"],
                                    ["Urgent improvement", "urgent improvement"],
                                ];
                                const ofstedLine = (o) => {
                                    if (!o) return null;
                                    const when = o.inspected
                                        ? new Date(o.inspected).toLocaleDateString("en-GB", {
                                              month: "short",
                                              year: "numeric",
                                          })
                                        : null;
                                    if (o.framework === "graded") {
                                        return "Ofsted " + o.overall + (when ? ", " + when : "");
                                    }
                                    if (o.framework === "report-card") {
                                        const g = o.grades || {};
                                        const counts = STANDARDS.map(([key, word]) => {
                                            const n = Object.keys(g).filter((k) => g[k] === key).length;
                                            return n ? n + " " + word : null;
                                        }).filter(Boolean);
                                        const safeguarding =
                                            o.safeguarding === "Not met" ? " · safeguarding not met" : "";
                                        return (
                                            "Ofsted report card" +
                                            (counts.length ? ": " + counts.join(", ") : "") +
                                            (when ? ", " + when : "") +
                                            safeguarding
                                        );
                                    }
                                    return "No current Ofsted grade";
                                };

                                const phase = (rows, title) => (
                                    <SchoolPhase>
                                        <h3>{title}</h3>
                                        {rows.length === 0 ? (
                                            <Reason>None within {sc.radiusMiles} miles.</Reason>
                                        ) : (
                                            <NearList>
                                                {rows.map((s, i) => (
                                                    <NearRow
                                                        key={s.name + s.metres}
                                                        beyondCap={!showAllSchools && i >= CAP}
                                                    >
                                                        <NearName>{s.name}</NearName>
                                                        <NearDistance>{distanceLabel(s.metres, s.miles)}</NearDistance>
                                                        <NearDetail>
                                                            {s.type}
                                                            {s.allThrough && " · all-through"}
                                                            {ofstedLine(s.ofsted) && (
                                                                <OfstedChip
                                                                    flag={
                                                                        s.ofsted.safeguarding === "Not met" ||
                                                                        s.ofsted.overall === "Inadequate"
                                                                    }
                                                                >
                                                                    {ofstedLine(s.ofsted)}
                                                                </OfstedChip>
                                                            )}
                                                        </NearDetail>
                                                    </NearRow>
                                                ))}
                                            </NearList>
                                        )}
                                    </SchoolPhase>
                                );
                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Schools</h2>
                                            <Tag ok={live}>
                                                {live
                                                    ? total + " within " + milesLabel(sc.radiusMiles)
                                                    : sc.status === "out-of-area"
                                                    ? "England only"
                                                    : "Not loaded"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    <SchoolColumns>
                                                        {phase(sc.primary, "Primary")}
                                                        {phase(sc.secondary, "Secondary")}
                                                    </SchoolColumns>
                                                    <BandsFoot>
                                                        Open primary and secondary schools whose{" "}
                                                        <strong>recorded address</strong> is within{" "}
                                                        {sc.radiusMiles} miles, as the crow flies. That is not a
                                                        walking distance, and it is not a catchment area &mdash;
                                                        admission is set by each school and by the council, and
                                                        being close by does not mean a place.{" "}
                                                        {(sc.primary.length > CAP || sc.secondary.length > CAP) && (
                                                            <span className="no-print">
                                                                <LinkButton
                                                                    type="button"
                                                                    onClick={() => setShowAllSchools(!showAllSchools)}
                                                                >
                                                                    {showAllSchools
                                                                        ? "Show the nearest " + CAP
                                                                        : "Show all " + total}
                                                                </LinkButton>
                                                                .{" "}
                                                            </span>
                                                        )}
                                                        {/*
                                                         * Most people still expect one word, and will read a
                                                         * report card as a school that somehow failed to get
                                                         * graded. Saying why the format changed costs two
                                                         * sentences and prevents that.
                                                         */}
                                                        <strong>Ofsted no longer gives schools a single word.</strong>{" "}
                                                        Inspections from November 2025 produce a report card
                                                        grading several areas separately, on a scale from
                                                        exceptional to urgent improvement, so those schools are
                                                        shown as the count at each standard rather than as a
                                                        verdict. A school inspected before that still carries its
                                                        old overall grade, dated, and one with neither has had no
                                                        graded inspection under either framework. None of it is a
                                                        judgement about whether your child would get in.{" "}
                                                        Source: Get Information about Schools, Department for
                                                        Education; inspection outcomes from Ofsted
                                                        {sc.ofstedRelease ? ", " + sc.ofstedRelease : ""}.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{sc.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {showPanel("shops") && report.shops && (
                            (() => {
                                const sp = report.shops || {};
                                const live = sp.status === "found";
                                const markets = live ? sp.supermarkets : [];
                                const others = live ? sp.shops : [];
                                const CAP = 6;
                                /*
                                 * The API trims the general retailers to the
                                 * nearest ten, so the heading says ten OF how
                                 * many rather than implying ten is all there is.
                                 */
                                const group = (rows, title, total) => (
                                    <SchoolPhase>
                                        <h3>
                                            {title}
                                            {total > rows.length && " — nearest " + rows.length + " of " + total}
                                        </h3>
                                        {rows.length === 0 ? (
                                            <Reason>None within {sp.radiusMiles} miles.</Reason>
                                        ) : (
                                            <NearList>
                                                {rows.map((s, i) => (
                                                    <NearRow
                                                        key={s.name + s.metres + i}
                                                        beyondCap={!showAllShops && i >= CAP}
                                                    >
                                                        <NearName>{s.name}</NearName>
                                                        <NearDistance>
                                                            {distanceLabel(s.metres, s.miles)}
                                                        </NearDistance>
                                                        {s.address && <NearDetail>{s.address}</NearDetail>}
                                                    </NearRow>
                                                ))}
                                            </NearList>
                                        )}
                                    </SchoolPhase>
                                );
                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Supermarkets and food shops</h2>
                                            <Tag ok={live && markets.length + others.length > 0}>
                                                {live
                                                    ? markets.length +
                                                      " supermarket" +
                                                      (markets.length === 1 ? "" : "s")
                                                    : "Unavailable"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    <SchoolColumns>
                                                        {group(markets, "Supermarkets", sp.supermarketsTotal)}
                                                        {group(others, "Other food shops", sp.shopsTotal)}
                                                    </SchoolColumns>
                                                    <BandsFoot>
                                                        From the Food Standards Agency&rsquo;s food hygiene
                                                        register, which lists businesses that{" "}
                                                        <strong>handle food</strong> &mdash; supermarkets, corner
                                                        shops, bakers, butchers. A clothes shop, a bank or a
                                                        pharmacy that sells no food will not appear, so this is not
                                                        a complete picture of the high street. Hygiene ratings are
                                                        published alongside this data and are deliberately not
                                                        shown.{" "}
                                                        {(markets.length > CAP || others.length > CAP) && (
                                                            <span className="no-print">
                                                                <LinkButton
                                                                    type="button"
                                                                    onClick={() => setShowAllShops(!showAllShops)}
                                                                >
                                                                    {showAllShops
                                                                        ? "Show the nearest " + CAP
                                                                        : "Show all " +
                                                                          (markets.length + others.length)}
                                                                </LinkButton>
                                                                .{" "}
                                                            </span>
                                                        )}
                                                        Source: Food Standards Agency.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{sp.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {showPanel("greenSpaces") && report.greenSpaces && (
                            (() => {
                                const gs = report.greenSpaces || {};
                                const live = gs.status === "found";
                                const sites = live ? gs.sites : [];
                                const CAP = 6;
                                // A pocket garden and a common are both "green
                                // space"; the size is what tells them apart.
                                const size = (h) =>
                                    h >= 10 ? Math.round(h) + " ha" : h >= 1 ? h + " ha" : null;
                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Parks and green spaces</h2>
                                            <Tag ok={live && sites.length > 0}>
                                                {live
                                                    ? sites.length + " within " + milesLabel(gs.radiusMiles)
                                                    : "Not loaded"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    {sites.length === 0 ? (
                                                        <Reason>
                                                            No park, playing field, play space or allotment within{" "}
                                                            {gs.radiusMiles} miles.
                                                        </Reason>
                                                    ) : (
                                                        <NearList>
                                                            {sites.map((s, i) => (
                                                                <NearRow
                                                                    key={s.name + s.func + s.metres + i}
                                                                    beyondCap={!showAllGreen && i >= CAP}
                                                                >
                                                                    <NearName>
                                                                        {s.name || (
                                                                            <em style={{ fontWeight: 400 }}>
                                                                                Unnamed {s.func.toLowerCase()}
                                                                            </em>
                                                                        )}
                                                                    </NearName>
                                                                    <NearDistance>
                                                                        {distanceLabel(s.metres, s.miles)}
                                                                    </NearDistance>
                                                                    {/*
                                                                     * An unnamed site already says what it
                                                                     * is in place of a name, so repeating
                                                                     * the function underneath is noise.
                                                                     */}
                                                                    <NearDetail>
                                                                        {s.name && s.func}
                                                                        {size(s.hectares) &&
                                                                            (s.name ? " · " : "") +
                                                                                "about " +
                                                                                size(s.hectares)}
                                                                    </NearDetail>
                                                                </NearRow>
                                                            ))}
                                                        </NearList>
                                                    )}
                                                    <BandsFoot>
                                                        Distance is to the <strong>edge</strong> of the site, not its
                                                        centre, so a park you live beside reads as adjacent. Sizes are
                                                        taken from the site&rsquo;s extent and are approximate.{" "}
                                                        {sites.length > CAP && (
                                                            <span className="no-print">
                                                                <LinkButton
                                                                    type="button"
                                                                    onClick={() => setShowAllGreen(!showAllGreen)}
                                                                >
                                                                    {showAllGreen
                                                                        ? "Show the nearest " + CAP
                                                                        : "Show all " + sites.length}
                                                                </LinkButton>
                                                                .{" "}
                                                            </span>
                                                        )}
                                                        Source: OS Open Greenspace.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{gs.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {showPanel("stations") && report.transport && (
                            (() => {
                                const tr = report.transport || {};
                                const live = tr.status === "found";
                                const stations = live ? tr.stations : [];
                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Stations</h2>
                                            <Tag ok={live && stations.length > 0}>
                                                {live
                                                    ? stations.length + " within " + milesLabel(tr.stationsRadiusMiles)
                                                    : "Unavailable"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    {stations.length === 0 ? (
                                                        <Reason>
                                                            No station within {tr.stationsRadiusMiles} miles of this
                                                            postcode.
                                                        </Reason>
                                                    ) : (
                                                        <NearList>
                                                            {stations.map((s) => (
                                                                <NearRow key={s.name}>
                                                                    <NearName>{s.name}</NearName>
                                                                    <NearDistance>{distanceLabel(s.metres, s.miles)}</NearDistance>
                                                                    <NearDetail>
                                                                        {s.lines.map((l) => (
                                                                            <LineChip
                                                                                key={l}
                                                                                colour={LINE_COLOURS[l]}
                                                                                dark={
                                                                                    DARK_TEXT_LINES.indexOf(l) !== -1
                                                                                }
                                                                            >
                                                                                {l}
                                                                            </LineChip>
                                                                        ))}
                                                                        {s.lines.length === 0 && s.modes.join(", ")}
                                                                    </NearDetail>
                                                                </NearRow>
                                                            ))}
                                                        </NearList>
                                                    )}
                                                    <BandsFoot>
                                                        Straight-line distance to the station, not a walking route.
                                                        Powered by TfL Open Data.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{tr.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {showPanel("buses") && report.transport && (
                            (() => {
                                const tr = report.transport || {};
                                /*
                                 * Buses now come from two places. Inside London
                                 * TfL supplies the stops AND the routes calling
                                 * at each. Everywhere else NaPTAN supplies the
                                 * stops and nothing supplies the routes, which
                                 * is what `buses.routes` reports.
                                 *
                                 * The distinction has to reach the reader. An
                                 * empty routes list rendered the same way as a
                                 * London one would assert that no bus calls
                                 * here, which is usually false.
                                 */
                                const buses = tr.buses || {};
                                const live = tr.status === "found" && buses.status === "found";
                                const hasRoutes = live && buses.routes !== false;
                                const stops = live ? tr.busStops : [];
                                const CAP = 6;
                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Buses</h2>
                                            <Tag ok={live && stops.length > 0}>
                                                {live
                                                    ? hasRoutes
                                                        ? tr.routes.length + " routes"
                                                        : stops.length + (stops.length === 1 ? " stop" : " stops")
                                                    : "Unavailable"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    {stops.length === 0 ? (
                                                        <Reason>
                                                            No bus stop within {tr.busesRadiusMiles} miles of this
                                                            postcode.
                                                        </Reason>
                                                    ) : (
                                                        <>
                                                            {hasRoutes && (
                                                                <EstimateNote style={{ margin: "0 0 0.9rem" }}>
                                                                    Routes you can reach on foot:{" "}
                                                                    {tr.routes.map((r, i) => (
                                                                        <span key={r}>
                                                                            {i > 0 && " "}
                                                                            <RouteChip>{r}</RouteChip>
                                                                        </span>
                                                                    ))}
                                                                </EstimateNote>
                                                            )}
                                                            <NearList>
                                                                {stops.map(
                                                                    (s, i) => (
                                                                        <NearRow
                                                                            key={s.name + s.metres}
                                                                            beyondCap={!showAllStops && i >= CAP}
                                                                        >
                                                                            <NearName>{s.name}</NearName>
                                                                            <NearDistance>{distanceLabel(s.metres, s.miles)}</NearDistance>
                                                                            <NearDetail>
                                                                                {/* London: which buses call here.
                                                                                    Elsewhere: which of the stops on
                                                                                    this street it is, since the two
                                                                                    directions share a name. */}
                                                                                {hasRoutes
                                                                                    ? (s.routes || []).map((r) => (
                                                                                          <RouteChip key={r}>{r}</RouteChip>
                                                                                      ))
                                                                                    : [s.indicator, s.street]
                                                                                          .filter(Boolean)
                                                                                          .join(" · ")}
                                                                            </NearDetail>
                                                                        </NearRow>
                                                                    )
                                                                )}
                                                            </NearList>
                                                        </>
                                                    )}
                                                    <BandsFoot>
                                                        Stops are grouped by name: every stand of a stop is a
                                                        separate record, so one junction can be four of them.
                                                        {/*
                                                         * "the routes above cover all N" is only true where
                                                         * there ARE routes above. Outside London the capped
                                                         * list is simply a capped list.
                                                         */}
                                                        {tr.busStopsCapped &&
                                                            " The nearest " +
                                                                stops.length +
                                                                " of " +
                                                                tr.busStopsTotal +
                                                                " are listed" +
                                                                (hasRoutes
                                                                    ? "; the routes above cover all " + tr.busStopsTotal + "."
                                                                    : ".")}{" "}
                                                        {!hasRoutes && (
                                                            <>
                                                                Which buses call at each stop is not published as
                                                                open data outside London, so this is where the
                                                                stops are rather than where they go.{" "}
                                                            </>
                                                        )}
                                                        {stops.length > CAP && (
                                                            <span className="no-print">
                                                                <LinkButton
                                                                    type="button"
                                                                    onClick={() => setShowAllStops(!showAllStops)}
                                                                >
                                                                    {showAllStops
                                                                        ? "Show the nearest " + CAP
                                                                        : "Show all " + stops.length}
                                                                </LinkButton>
                                                                .{" "}
                                                            </span>
                                                        )}
                                                        {/* Credit whichever register actually answered. */}
                                                        {hasRoutes
                                                            ? "Powered by TfL Open Data."
                                                            : "Stop locations from NaPTAN, the Department for Transport’s national register of public transport access points."}
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{buses.reason || tr.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}



                            {has("comparables") && report.comparables && (
                            (() => {
                                const cp = report.comparables;
                                const live = cp.status === "found";
                                const none = cp.status === "none";
                                const s = cp.summary;
                                const indexedTo = cp.indexedTo
                                    ? new Date(cp.indexedTo + "-01T00:00:00Z").toLocaleDateString("en-GB", {
                                          month: "long",
                                          year: "numeric",
                                          timeZone: "UTC",
                                      })
                                    : null;
                                return (
                                    <Panel muted={!live}>
                                        <PanelHead muted={!live}>
                                            <h2>Comparable sales</h2>
                                            <Tag ok={live}>
                                                {/* The nearest range when there is one, so the
                                                    headline and the panel agree. */}
                                                {live
                                                    ? cp.near
                                                        ? money0(cp.near.lower) + " to " + money0(cp.near.upper)
                                                        : money0(s.lower) + " to " + money0(s.upper)
                                                    : none
                                                    ? "None in range"
                                                    : "Unavailable"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live}>
                                            {live ? (
                                                <>
                                                    {cp.near && (
                                                        <NearLead>
                                                            <h3>
                                                                {cp.near.scope === "postcode"
                                                                    ? "In this postcode"
                                                                    : "In this street"}
                                                            </h3>
                                                            <strong>{money0(cp.near.median)}</strong>
                                                            <span>
                                                                {money0(cp.near.lower)} &ndash;{" "}
                                                                {money0(cp.near.upper)} &middot;{" "}
                                                                {cp.near.count} sale
                                                                {cp.near.count === 1 ? "" : "s"}
                                                                {/* The near group may span a longer
                                                                    window than the sector figure
                                                                    below it, so it says which. */}
                                                                {cp.near.years
                                                                    ? " over " + cp.near.years + " years"
                                                                    : ""}
                                                            </span>
                                                        </NearLead>
                                                    )}

                                                    {/* The sector, which is context once there is
                                                        something nearer. Labelled so the two are
                                                        never read as the same claim. */}
                                                    {cp.near && (
                                                        <EstimateNote style={{ margin: "0 0 0.5rem" }}>
                                                            Across the wider area:
                                                        </EstimateNote>
                                                    )}

                                                    <Facts>
                                                        <div>
                                                            <dt>Middle half</dt>
                                                            <dd>
                                                                {money0(s.lower)} &ndash; {money0(s.upper)}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt>Median</dt>
                                                            <dd>{money0(s.median)}</dd>
                                                        </div>
                                                        <div>
                                                            <dt>Full range</dt>
                                                            <dd>
                                                                {money0(s.lowest)} &ndash; {money0(s.highest)}
                                                            </dd>
                                                        </div>
                                                        <div>
                                                            <dt>Sales used</dt>
                                                            <dd>{cp.total}</dd>
                                                        </div>
                                                    </Facts>

                                                    <EstimateNote style={{ margin: "0 0 0.9rem" }}>
                                                        {cp.typeMatched
                                                            ? cp.type + " sales in " + cp.sector
                                                            : "Sales in " + cp.sector + ", every property type"}{" "}
                                                        over the last {cp.years} years, each restated to{" "}
                                                        {indexedTo} prices.
                                                    </EstimateNote>

                                                    <NearList>
                                                        {cp.sales.map((x, i) => (
                                                            <NearRow
                                                                key={x.address + x.date + i}
                                                                beyondCap={!showAllComps && i >= 6}
                                                            >
                                                                <NearName>
                                                                    {x.address}
                                                                    {x.sameStreet && <Match>same street</Match>}
                                                                </NearName>
                                                                <NearDistance>{money0(x.indexed)}</NearDistance>
                                                                <NearDetail>
                                                                    {new Date(x.date).toLocaleDateString("en-GB", {
                                                                        month: "short",
                                                                        year: "numeric",
                                                                    })}
                                                                    {" · "}
                                                                    paid {money0(x.price)}
                                                                    {x.typeLabel ? " · " + x.typeLabel : ""}
                                                                </NearDetail>
                                                            </NearRow>
                                                        ))}
                                                    </NearList>

                                                    <BandsFoot>
                                                        <strong>Evidence, not a valuation.</strong> These are the
                                                        sales an appraisal would be built from, with the
                                                        arithmetic shown: each price is multiplied by how far the
                                                        local market has moved since it sold, from HM Land
                                                        Registry&rsquo;s House Price Index.{" "}
                                                        {cp.sales.length > 6 && (
                                                            <span className="no-print">
                                                                <LinkButton
                                                                    type="button"
                                                                    onClick={() => setShowAllComps(!showAllComps)}
                                                                >
                                                                    {showAllComps
                                                                        ? "Show the nearest 6"
                                                                        : "Show all " + cp.sales.length}
                                                                </LinkButton>
                                                                .{" "}
                                                            </span>
                                                        )}
                                                        That index is an area average across every size and type,
                                                        so it describes how the neighbourhood moved, not how this
                                                        home did &mdash; an extension or a bad survey moves a
                                                        property away from its area and this knows nothing about
                                                        either.
                                                        {!cp.typeMatched &&
                                                            " The certificate did not say what kind of home this is, so the set is not filtered by type."}
                                                        {!cp.marketOnly &&
                                                            " Land Registry also record transfers that were not open-market sales — repossessions, portfolio transfers — and this set cannot yet tell them apart, so a price far below the rest is probably one of those rather than a bargain."}
                                                        {cp.truncated &&
                                                            " The sector returned more sales than this summary considers, so it is drawn from the most recent of them."}
                                                        {cp.unindexed > 0 &&
                                                            " " +
                                                                cp.unindexed +
                                                                " sale" +
                                                                (cp.unindexed === 1 ? " was" : "s were") +
                                                                " left out, being older than the index reaches."}{" "}
                                                        Contains HM Land Registry data &copy; Crown copyright and
                                                        database right 2026.
                                                    </BandsFoot>
                                                </>
                                            ) : none ? (
                                                <Reason>
                                                    No {cp.type ? cp.type.toLowerCase() + " " : ""}sale is recorded
                                                    in {cp.sector} in the last {cp.years} years, so there is
                                                    nothing to compare against.
                                                </Reason>
                                            ) : (
                                                <Reason>{cp.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}
                            {has("crime") && report.crime && (
                            (() => {
                                const cr = report.crime;
                                const live = cr.status === "found";
                                const none = cr.status === "none";
                                const top = (cr.categories || [])[0];
                                /*
                                 * The month the register published, not the month
                                 * we asked for. Forces report monthly and the
                                 * register waits for the last one, so this is
                                 * routinely two months back - and saying which
                                 * month is the difference between a figure and a
                                 * number.
                                 */
                                const when = cr.month
                                    ? new Date(cr.month + "-01T00:00:00Z").toLocaleDateString("en-GB", {
                                          month: "long",
                                          year: "numeric",
                                          timeZone: "UTC",
                                      })
                                    : null;
                                return (
                                    <Panel muted={!live && !none}>
                                        <PanelHead muted={!live && !none}>
                                            <h2>Crime</h2>
                                            <Tag ok={live || none}>
                                                {live
                                                    ? cr.total.toLocaleString() + " in " + when
                                                    : none
                                                    ? "None recorded"
                                                    : "Unavailable"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live && !none}>
                                            {live ? (
                                                <>
                                                    <NearList>
                                                        {cr.categories.map((c, i) => (
                                                            <NearRow key={c.key} beyondCap={!showAllCrime && i >= 6}>
                                                                <NearName>{c.label}</NearName>
                                                                <NearDistance>{c.count.toLocaleString()}</NearDistance>
                                                            </NearRow>
                                                        ))}
                                                    </NearList>
                                                    <BandsFoot>
                                                        Crimes recorded within one mile of the postcode in {when}.
                                                        That radius is the register&rsquo;s own and cannot be
                                                        narrowed, so it covers a wider area than the panels above.{" "}
                                                        {cr.categories.length > 6 && (
                                                            <span className="no-print">
                                                                <LinkButton
                                                                    type="button"
                                                                    onClick={() => setShowAllCrime(!showAllCrime)}
                                                                >
                                                                    {showAllCrime
                                                                        ? "Show the top 6"
                                                                        : "Show all " + cr.categories.length}
                                                                </LinkButton>
                                                                .{" "}
                                                            </span>
                                                        )}
                                                        These are reports to the police, not convictions, and a high
                                                        street collects shoplifting the way a quiet road does not
                                                        {top ? " — " + top.label.toLowerCase() + " leads here" : ""}.
                                                        Source: data.police.uk.
                                                    </BandsFoot>
                                                </>
                                            ) : none ? (
                                                <Reason>
                                                    No crimes were recorded within a mile of this postcode in {when}.
                                                </Reason>
                                            ) : (
                                                <Reason>{cr.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {has("planning") && report.planning && (
                            (() => {
                                const pl = report.planning;
                                const live = pl.status === "found";
                                const none = pl.status === "none";
                                const flood = pl.flood;
                                const desig = pl.designations || [];

                                /*
                                 * Two different floods, and the card has to keep
                                 * them apart. `flood` is the Flood Map for
                                 * Planning - rivers and the sea. `sw` is the
                                 * Environment Agency's surface water model, rain
                                 * overwhelming the drains, which is the commoner
                                 * cause in London and was the half this card
                                 * used to say it did not check.
                                 */
                                const sw = pl.surfaceWater || null;
                                const swBand = sw && sw.status === "found" ? sw.band : null;
                                const swNone = sw && sw.status === "none";
                                const SW_WORDS = {
                                    High: "a 1 in 30 chance or greater in any year",
                                    Medium: "between a 1 in 100 and a 1 in 30 chance in any year",
                                    Low: "between a 1 in 1,000 and a 1 in 100 chance in any year",
                                };
                                return (
                                    <Panel muted={!live && !none}>
                                        <PanelHead muted={!live && !none}>
                                            <h2>Planning and flood</h2>
                                            {/*
                                              * The tag leads with whichever flood
                                              * answer is worse, because that is
                                              * what someone scanning the page is
                                              * looking for - a high surface water
                                              * risk matters more than the count of
                                              * conservation areas, and used not to
                                              * appear at all.
                                              */}
                                            <Tag ok={live || none}>
                                                {flood
                                                    ? "Flood zone " + flood.zone
                                                    : swBand && swBand !== "Low"
                                                    ? swBand + " surface water risk"
                                                    : live
                                                    ? desig.length + (desig.length === 1 ? " designation" : " designations")
                                                    : none
                                                    ? "None recorded"
                                                    : "Unavailable"}
                                            </Tag>
                                        </PanelHead>
                                        <PanelBody muted={!live && !none}>
                                            {live || none ? (
                                                <>
                                                    {flood && (
                                                        <Facts>
                                                            <div>
                                                                <dt>Flood zone</dt>
                                                                <dd>{flood.zone}</dd>
                                                            </div>
                                                            {flood.models.length > 0 && (
                                                                <div>
                                                                    <dt>Modelled from</dt>
                                                                    <dd>{flood.models.join(", ")}</dd>
                                                                </div>
                                                            )}
                                                        </Facts>
                                                    )}
                                                    {desig.length > 0 && (
                                                        <NearList>
                                                            {desig.map((d, i) => (
                                                                <NearRow key={d.dataset + i}>
                                                                    <NearName>{d.label}</NearName>
                                                                    <NearDistance />
                                                                    {d.name && (
                                                                        <NearDetail>
                                                                            {d.url ? (
                                                                                <a
                                                                                    href={d.url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                >
                                                                                    {d.name}
                                                                                </a>
                                                                            ) : (
                                                                                d.name
                                                                            )}
                                                                        </NearDetail>
                                                                    )}
                                                                </NearRow>
                                                            ))}
                                                        </NearList>
                                                    )}
                                                    {!flood && desig.length === 0 && (
                                                        <Reason>
                                                            Nothing is recorded against this point on the Planning
                                                            Data Platform.
                                                        </Reason>
                                                    )}
                                                    {/*
                                                     * SAY THE NEGATIVE OUT LOUD, for both floods. A card headed
                                                     * "Planning and flood" that lists a conservation area and
                                                     * never mentions flood leaves the reader unable to tell "we
                                                     * checked and there is none" from "we did not check", and
                                                     * silence reads as the second.
                                                     *
                                                     * Rivers first, then surface water, each stated whichever
                                                     * way the answer came out - this is the half of the card
                                                     * most addresses get, and until now it was blank.
                                                     */}
                                                    <SwNote>
                                                        {!flood && (
                                                            <div>
                                                                <strong>Rivers and the sea:</strong> no flood zone
                                                                is recorded at this point.
                                                            </div>
                                                        )}
                                                        {swBand && (
                                                            <div>
                                                                <strong>Surface water:</strong> {swBand.toLowerCase()}{" "}
                                                                risk &mdash; {SW_WORDS[swBand] || "see the Environment Agency's own map"}.
                                                            </div>
                                                        )}
                                                        {swNone && (
                                                            <div>
                                                                <strong>Surface water:</strong> below the lowest
                                                                band the Environment Agency map, which is under a 1
                                                                in 1,000 chance in any year.
                                                            </div>
                                                        )}
                                                        {sw && sw.status === "unavailable" && (
                                                            <div>
                                                                <strong>Surface water:</strong> the Environment
                                                                Agency map could not be reached, so this is not
                                                                checked.
                                                            </div>
                                                        )}
                                                    </SwNote>
                                                    <BandsFoot>
                                                        {flood
                                                            ? "Flood zone 2 is land between a 1 in 1,000 and a 1 in 100 annual chance of river flooding; zone 3 is a 1 in 100 chance or greater. It describes the land, not the building, and takes no account of defences already built. "
                                                            : ""}
                                                        {/*
                                                         * The zones are the Environment Agency's Flood Map for
                                                         * Planning, which is rivers and the sea. Surface water is
                                                         * a different map we do not hold, and in London it is the
                                                         * commoner cause - so an unqualified "no flood zone"
                                                         * would be the most misleading true sentence on the page.
                                                         */}
                                                        {/*
                                                         * What neither map covers. Sewers, highway drains and
                                                         * groundwater are named by the Environment Agency itself
                                                         * as outside the surface water model, and a reader who
                                                         * has just been told two kinds of flood were checked
                                                         * will otherwise assume all of them were.
                                                         */}
                                                        Flood zones are the Environment Agency&rsquo;s Flood Map
                                                        for Planning, which covers rivers and the sea; surface
                                                        water is their separate national model of rain
                                                        overwhelming the ground. Neither covers sewers, highway
                                                        drains or groundwater. Both describe an area of land
                                                        rather than a building &mdash; the Environment Agency say
                                                        the surface water map is not suitable for assessing a
                                                        single property &mdash; and neither accounts for defences
                                                        already built.{" "}
                                                        Local planning authorities publish into this register at
                                                        their own pace, so nothing recorded here is not the same as
                                                        nothing there &mdash; a solicitor&rsquo;s search is still the
                                                        answer before you buy. Source: planning.data.gov.uk.
                                                    </BandsFoot>
                                                </>
                                            ) : (
                                                <Reason>{pl.reason}</Reason>
                                            )}
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            {/*
                             * LETTING IT OUT. Three obligations people routinely run
                             * together, kept apart here because conflating them is how a
                             * landlord ends up unlicensed while believing otherwise.
                             *
                             * The mandatory HMO threshold is national and depends on how
                             * the property is LET, which nothing about an address can
                             * tell us - so it is a rule for the reader to apply, not an
                             * answer. Selective and additional schemes depend entirely on
                             * WHERE it is and have no national register: designated
                             * council by council, often ward by ward, with their own
                             * start and end dates. So this names the council and links to
                             * it, and says why that is a link rather than an answer.
                             *
                             * Article 4 is the one line here that is about this address,
                             * and it comes free from the planning lookup.
                             */}
                            {has("licensing") && report.licensing && (
                            (() => {
                                const lc = report.licensing;
                                return (
                                    <Panel>
                                        <PanelHead>
                                            <h2>Letting it out</h2>
                                        </PanelHead>
                                        <PanelBody>
                                            {lc.rentSmartWales && (
                                                <>
                                                    <SubHead>Wales</SubHead>
                                                    <Reason>
                                                        Every landlord in Wales must register with Rent Smart
                                                        Wales, and must hold a licence as well if they let or
                                                        manage the property themselves rather than through a
                                                        licensed agent.{" "}
                                                        <a
                                                            href="https://www.rentsmart.gov.wales/"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Rent Smart Wales
                                                        </a>
                                                        .
                                                    </Reason>
                                                </>
                                            )}

                                            <SubHead>Large HMOs</SubHead>
                                            <Reason>
                                                A licence is required anywhere in England and Wales if the
                                                property is let to five or more people forming more than one
                                                household, who share a toilet, bathroom or kitchen.
                                            </Reason>

                                            <SubHead>Smaller lets</SubHead>
                                            <Reason>
                                                Councils can require a licence for smaller shared homes, or for
                                                every rented home in part of the area. These schemes are set
                                                locally, cover particular wards or streets, and start and end on
                                                their own dates &mdash; so they have to be checked against{" "}
                                                {lc.council || "the council"} directly. There is no national
                                                register of them, which is why this is a link rather than an
                                                answer.{" "}
                                                {lc.hmoLicenceUrl && (
                                                    <a
                                                        href={lc.hmoLicenceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Licensing for {lc.council || "this council"}
                                                    </a>
                                                )}
                                                {lc.councilUrl && (
                                                    <>
                                                        {" "}&middot;{" "}
                                                        <a
                                                            href={lc.councilUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            council website
                                                        </a>
                                                    </>
                                                )}
                                            </Reason>

                                            {/*
                                             * Three states. Silence would be read as "no", and a
                                             * wrong "no" here tells someone they need no planning
                                             * permission when they do.
                                             */}
                                            {lc.article4 === true && (
                                                <>
                                                    <SubHead>Article 4 direction</SubHead>
                                                    <Reason>
                                                        This address is in an Article 4 area. Converting a house
                                                        to a small shared home normally needs no planning
                                                        permission; here it does &mdash; separately from, and in
                                                        addition to, any licence.
                                                    </Reason>
                                                </>
                                            )}
                                            {lc.article4 === null && (
                                                <>
                                                    <SubHead>Article 4 direction</SubHead>
                                                    <Reason>
                                                        We couldn&rsquo;t check this one &mdash; the planning
                                                        platform didn&rsquo;t answer. An Article 4 direction
                                                        removes the automatic right to convert a house into a
                                                        small shared home, so it is worth asking{" "}
                                                        {lc.council || "the council"} directly before you do.
                                                    </Reason>
                                                </>
                                            )}

                                            <BandsFoot>
                                                General guidance, not advice on this property. The council
                                                decides what it requires.
                                            </BandsFoot>
                                        </PanelBody>
                                    </Panel>
                                );
                            })()
                            )}

                            </PanelGrid>

                            {/*
                             * PRODUCT BRAND ONLY, and the gate is brand.js
                             * rather than a hostname read here - section 6f.
                             * The agency sells valuations carried out by
                             * people; its own site offering an automated one
                             * beside its instruction fees would be saying two
                             * things at once.
                             *
                             * Needs a chosen certificate, not just a postcode:
                             * the estimate is a price per square metre times
                             * THIS property's floor area, and the floor area
                             * comes off the certificate.
                             *
                             * Deliberately not .no-print. A valuation that
                             * vanished from the PDF would leave the caveats
                             * printing with no figure to caveat.
                             *
                             * KEYED ON THE CERTIFICATE so a different address
                             * gets a fresh panel. The acknowledgement ticks
                             * are given about ONE property, and carrying them
                             * to the next would be recording a consent nobody
                             * gave.
                             */}
                            {brand.key === "product" && chosen && (
                                <div id={VALUATION_ANCHOR}>
                                    <AiValuation
                                        key={chosen.certificateNumber}
                                        postcode={report.postcode}
                                        cert={chosen}
                                        initialToken={valuationToken}
                                        onToken={setValuationToken}
                                        shareUrl={reportUrl({
                                            postcode: sharePostcode,
                                            cert: chosen,
                                            valuationToken: valuationToken,
                                        })}
                                        onPrint={printPropertyReport}
                                        onEmail={openEmailForm}
                                    />
                                </div>
                            )}

                            <Reason style={{ marginTop: "2rem" }}>
                                Contains public sector information licensed under the Open Government Licence v3.0.
                                Location data from postcodes.io, derived from ONS and Ordnance Survey open data.
                                School data from Get Information about Schools, Department for Education, with
                                inspection outcomes from Ofsted. Shop data from
                                the Food Standards Agency. Crime data from data.police.uk. Planning and flood
                                designations from the Planning Data Platform, planning.data.gov.uk. Surface water
                                flood risk from the Environment Agency&rsquo;s national map. Local authority
                                identified using the GOV.UK local council lookup; licensing requirements
                                are set by that council and stated by it. Water company
                                areas from the Environment Agency&rsquo;s Water Resource Zones; average household
                                water bills from Water UK. Broadband spend from Ofcom. Contains Environment Agency
                                information &copy; Environment Agency and database right. Contains
                                OS data &copy; Crown copyright and database right 2026.{" "}
                                {/*
                                 * TfL's wording is prescribed by their open data
                                 * terms, including the Ordnance Survey and Geomni
                                 * lines and their years. Reproduce as given.
                                 */}
                                Powered by TfL Open Data. Bus stop locations outside London from NaPTAN,
                                the Department for Transport&rsquo;s national public transport access node
                                register. Contains OS data &copy; Crown copyright and database
                                rights 2016 and Geomni UK Map data &copy; and database rights 2019. Figures are
                                indicative and are not a substitute for your council tax bill or a professional
                                survey.
                            </Reason>

                            {/*
                             * The hero carries this on screen; the hero does not
                             * print. The licence requires the notice to travel
                             * with the address data, so it is repeated here for
                             * anyone whose only copy is the PDF.
                             */}
                            {addressOptions.length > 0 && (
                                <PrintNotice>
                                    <AddressLicence />
                                </PrintNotice>
                            )}

                            {/*
                             * Who produced this, on the copy that leaves the
                             * site. The footer carries it on screen and the
                             * footer does not print - so a PDF forwarded to a
                             * buyer, a solicitor or a landlord would otherwise
                             * name a brand and no company at all. Both brands
                             * are trading names of the same operator, which is
                             * why the line is built from the brand rather than
                             * written out twice.
                             */}
                            <PrintColophon>
                                {brand.name} &middot; {brandHost}
                                <br />
                                &copy; {new Date().getFullYear()} Property People London Ltd.{" "}
                                {brand.name} is a trading name of Property People London Ltd
                                (Reg No: 07355433).
                            </PrintColophon>
                            </Container>
                        </>
                    )}
            </Body>

            <div className="no-print">
                <SiteFooter />
            </div>
        </>
    );
}
